import React, { useState, useEffect } from 'react';
import { format, isValid, parseISO, subDays, startOfMonth } from 'date-fns';
import * as XLSX from 'xlsx';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import PaiAccountMappings from '../components/PaiAccountMappings';
import PaiFrequentAccounts from '../components/PaiFrequentAccounts';
import NachaSettingsForm from '../components/NachaSettingsForm';
import HgGhMatcher from '../components/HgGhMatcher';
import InterchangeTab from '../components/InterchangeTab';
import BankReconciliationTab from '../components/BankReconciliationTab';
import SettlementBatches from './SettlementBatches';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  Chip,
  IconButton,
  Switch,
  FormControlLabel,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Menu,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  Checkbox,
  FormGroup,
  Autocomplete
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  AccountBalance as AccountIcon,
  Settings as SettingsIcon,
  LocationOn as LocationIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as FileIcon,
  Description,
  AccountBalance as BankIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  DateRange as DateRangeIcon,
  TrendingUp as TrendingUpIcon,
  LocalAtm as AtmIcon,
  Cancel as CancelIcon,
  Star as StarIcon,
  Assessment as AssessmentIcon,
  CompareArrows as CompareArrowsIcon,
  GetApp as GetAppIcon,
  Search as SearchIcon,
  SaveAlt,
  Sync as SyncIcon,
  CloudUpload as UploadIcon,
  Preview as PreviewIcon,
  History as HistoryIcon,
  SwapHoriz as SwapHorizIcon,
  PlayArrow as PlayArrowIcon
} from '@mui/icons-material';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { transactionTypeColor } from '../utils/transactionTypes';

const PaiDashboard = () => {
  const [tabValue, setTabValue] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // NACHA export state
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [nachaSettingsOpen, setNachaSettingsOpen] = useState(false);
  const [nachaSettings, setNachaSettings] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  
  // Terminal Reports state
  const [terminalReportDate, setTerminalReportDate] = useState(subDays(new Date(), 1));
  const [terminalReportLoading, setTerminalReportLoading] = useState(false);
  const [terminalStatusData, setTerminalStatusData] = useState(null);
  const [terminalTransactionData, setTerminalTransactionData] = useState(null);
  const [trxLogLoading, setTrxLogLoading] = useState(false);
  const [trxLogData, setTrxLogData] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileData, setReconcileData] = useState(null);
  
  // Reconciliation Export state
  const [reconciliationLocation, setReconciliationLocation] = useState('');
  const [reconciliationStartDate, setReconciliationStartDate] = useState(subDays(new Date(), 7));
  const [reconciliationEndDate, setReconciliationEndDate] = useState(new Date());
  const [reconciliationData, setReconciliationData] = useState(null);
  const [reconciliationWarning, setReconciliationWarning] = useState('');
  const [paiLocations, setPaiLocations] = useState([]);
  const [locationSearchTerm, setLocationSearchTerm] = useState('');
  
  // Dashboard state (from ATM Dashboard)
  const [viewMode, setViewMode] = useState('single');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    dailyStats: {
      totalApproved: 0,
      totalTransactions: 0,
      totalWithdrawalAmount: 0,
      totalSurcharge: 0,
      uniqueTerminals: 0
    },
    responseBreakdown: [],
    topTerminals: [],
    availableDates: [],
    summaryStats: {
      totalTerminals: 0,
      daysWithData: 0,
      earliestDate: null,
      latestDate: null,
      totalTransactionsAllTime: 0
    }
  });
  
  // Settings state
  const [paiSettings, setPaiSettings] = useState({
    api_enabled: { value: 'false' },
    daily_schedule_enabled: { value: 'false' },
    schedule_time: { value: '04:00' }
  });
  const [updatingSettings, setUpdatingSettings] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [lastDownloadStats, setLastDownloadStats] = useState(null);

  // PAI Download state
  const [downloadDate, setDownloadDate] = useState(subDays(new Date(), 1)); // Default to yesterday
  const [downloadStartDate, setDownloadStartDate] = useState(startOfMonth(new Date()));
  const [downloadEndDate, setDownloadEndDate] = useState(new Date());
  const [downloadMode, setDownloadMode] = useState('yesterday'); // 'yesterday', 'specific', 'range'
  const [selectedAccount, setSelectedAccount] = useState('TFIATMs'); // Default account
  const [availableAccounts, setAvailableAccounts] = useState([]);
  
  // Monthly Surcharge Export state
  const [exportMonth, setExportMonth] = useState(new Date().getMonth() + 1);
  const [exportYear, setExportYear] = useState(new Date().getFullYear());
  const [exportingMonthlySurcharge, setExportingMonthlySurcharge] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTypes, setSelectedTypes] = useState(['Settlement', 'Surcharge']); // Both selected by default

  // Manual Import state
  const [importFile, setImportFile] = useState(null);
  const [importProcessor, setImportProcessor] = useState('DNS');
  const [importPreview, setImportPreview] = useState(null);
  const [importParsing, setImportParsing] = useState(false);
  const [importSubmitting, setImportSubmitting] = useState(false);
  const [importHistory, setImportHistory] = useState([]);

  // Settlement Reports state (multi-processor)
  const [settlementLocations, setSettlementLocations] = useState([]); // legacy location-mode (kept for back-compat)
  // Processor-mode (admin-only, reconciled batches, 12am-12am). Switch Commerce only for now.
  const [settlementProcessor, setSettlementProcessor] = useState('SWITCH');
  // Default range trails by the 3-day reconciliation lag (data is complete by then).
  const [settlementStartDate, setSettlementStartDate] = useState(subDays(new Date(), 9));
  const [settlementEndDate, setSettlementEndDate] = useState(subDays(new Date(), 3));
  const [settlementData, setSettlementData] = useState(null);
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [allLocations, setAllLocations] = useState([]); // Locations from all processors

  // Run Settlement dialog state
  const [runSettlementOpen, setRunSettlementOpen] = useState(false);
  const [runSettlementDate, setRunSettlementDate] = useState(subDays(new Date(), 1)); // Default to yesterday
  const [runSettlementTypes, setRunSettlementTypes] = useState({ settlement: true, surcharge: true });
  const [runSettlementCutoff, setRunSettlementCutoff] = useState('processor'); // 'processor' (3pm cutoff) or 'calendar' (12am-12am)
  const [settlementReportTypes, setSettlementReportTypes] = useState(['SETL', 'SRCH']); // Both selected by default
  const [settlementDayType, setSettlementDayType] = useState('processor'); // 'processor' (3pm cutoff) or 'calendar' (midnight-midnight)

  // ACH Management tab state — transmission ledger (sent/failed files + S3 archive links)
  const [achTransmissions, setAchTransmissions] = useState([]);
  const [achLoading, setAchLoading] = useState(false);
  const [achSending, setAchSending] = useState(false);
  // Resend-ACH flow (bank didn't process a file): pick a SENT file, type CONFIRM, re-push same bytes
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [resendTxId, setResendTxId] = useState('');
  const [resendConfirmText, setResendConfirmText] = useState('');
  // Optional ACH effective-date override (YYYY-MM-DD); empty = next banking day (auto)
  const [achEffectiveDate, setAchEffectiveDate] = useState('');
  
  // Account mappings state
  const [mappings, setMappings] = useState([]);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [showAccountNumbers, setShowAccountNumbers] = useState(false);
  const [mappingFormData, setMappingFormData] = useState({
    businessCode: '',
    businessName: '',
    fullAccountNumber: '',
    fullRoutingNumber: '',
    terminalIds: '',
    notes: '',
    active: true
  });

  const { apiCall, generateNachaFile, getNachaSettings, saveNachaSettings } = useApi();
  const { hasPermission, userProfile } = useAuth();

  useEffect(() => {
    // Only load PAI data if user has PAI permissions
    if (hasPermission('manage_pai_accounts') || userProfile?.role === 'admin') {
      loadPaiSettings();
      loadMappings();
      fetchDashboardData();
      loadAvailableAccounts();
      fetchPaiLocations();
      fetchAllLocations(); // Load all locations from all processors
    } else {
      setLoading(false);
      // Don't set error message - just silently don't load the data
      // The user shouldn't even see this page if they don't have permission
    }
  }, []);
  
  useEffect(() => {
    // Only fetch dashboard data if user has PAI permissions and is on dashboard tab
    if (tabValue === 'overview' && (hasPermission('manage_pai_accounts') || userProfile?.role === 'admin')) {
      fetchDashboardData();
    }
  }, [selectedDate, startDate, endDate, viewMode, tabValue]);

  const loadPaiSettings = async () => {
    try {
      setLoading(true);
      const response = await apiCall('/api/v1/pai/settings', 'GET');
      if (response && response.settings) {
        setPaiSettings(response.settings);
      }
    } catch (error) {
      console.error('Failed to load PAI settings:', error);
      setError('Failed to load PAI settings');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableAccounts = async () => {
    // Use configured accounts directly - both have report GUIDs configured in backend
    setAvailableAccounts([
      { id: 'TFIATMs', name: 'TFIATMs', hasReportGuid: true },
      { id: 'Ross_Nolte', name: 'Ross_Nolte', hasReportGuid: true }
    ]);
    setSelectedAccount('TFIATMs'); // Default to TFIATMs
  };

  const updatePaiSettings = async (key, value) => {
    try {
      setUpdatingSettings(true);
      const newSettings = {
        ...paiSettings,
        [key]: { ...paiSettings[key], value: value.toString() }
      };
      
      const response = await apiCall('/api/v1/pai/settings', 'PUT', {
        settings: {
          api_enabled: newSettings.api_enabled.value,
          daily_schedule_enabled: newSettings.daily_schedule_enabled.value,
          schedule_time: newSettings.schedule_time.value
        }
      });
      
      if (response && response.settings) {
        setPaiSettings(response.settings);
        setError(null);
      }
    } catch (error) {
      console.error('Failed to update PAI settings:', error);
      setError('Failed to update PAI settings');
    } finally {
      setUpdatingSettings(false);
    }
  };

  const handleDownloadReport = async (action = 'downloadYesterday', date = null) => {
    try {
      setDownloadingReport(true);
      const payload = {
        action,
        account: selectedAccount // Include selected account
      };

      if (action === 'download' && date) {
        payload.date = format(date, 'MM/dd/yyyy');
      } else if (action === 'downloadRange') {
        payload.startDate = format(downloadStartDate, 'MM/dd/yyyy');
        payload.endDate = format(downloadEndDate, 'MM/dd/yyyy');
      }

      const response = await apiCall('/api/v1/pai/download-report', 'POST', payload);
      setError(null);

      // Display download statistics
      if (action === 'downloadRange') {
        const message = `Downloaded ${response.totalRecords || 0} total records from ${payload.startDate} to ${payload.endDate}`;
        setSuccess(message);
        setLastDownloadStats({
          date: `${payload.startDate} to ${payload.endDate}`,
          recordsProcessed: response.totalRecords || 0,
          duplicates: 0,
          totalRecords: response.totalRecords || 0,
          timestamp: new Date().toLocaleString()
        });
        console.log(message);
      } else {
        const recordsProcessed = response.recordsProcessed || 0;
        const duplicates = response.recordsSkipped || 0;
        const totalRecords = response.totalRecords || 0;
        const dateStr = response.date || payload.date || 'yesterday';

        let message = `Download complete for ${dateStr}: `;
        message += `${recordsProcessed} new records inserted`;
        if (duplicates > 0) {
          message += `, ${duplicates} duplicates skipped`;
        }
        message += ` (${totalRecords} total records found)`;

        setSuccess(message);
        setLastDownloadStats({
          date: dateStr,
          recordsProcessed,
          duplicates,
          totalRecords,
          timestamp: new Date().toLocaleString()
        });
        console.log(message);
      }
    } catch (error) {
      console.error('Failed to download PAI report:', error);
      setError('Failed to download PAI report');
    } finally {
      setDownloadingReport(false);
    }
  };
  
  const handleExportMonthlySurcharge = async () => {
    try {
      setExportingMonthlySurcharge(true);
      setError(null);
      
      const response = await apiCall('/api/v1/pai/monthly-surcharge-export', 'GET', null, {
        month: exportMonth,
        year: exportYear
      });
      
      if (response.csv) {
        // Create and download CSV file
        const blob = new Blob([response.csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', response.filename || `monthly_surcharge_${exportMonth}_${exportYear}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        // Export completed successfully - no alert needed (timestamp: ${Date.now()})
        console.log(`Monthly surcharge export completed: ${response.recordCount || 0} terminals for ${response.month} ${response.year}`);
      } else {
        setError('No data received from export');
      }
    } catch (error) {
      console.error('Failed to export monthly surcharge:', error);
      setError('Failed to export monthly surcharge report');
    } finally {
      setExportingMonthlySurcharge(false);
    }
  };

  const loadMappings = async () => {
    try {
      const response = await apiCall('/api/v1/pai/account-mappings', 'GET');
      if (response && response.mappings) {
        setMappings(response.mappings);
      }
    } catch (error) {
      console.error('Failed to load PAI mappings:', error);
      setError('Failed to load PAI account mappings');
    }
  };

  const handleOpenMappingDialog = (mapping = null) => {
    if (mapping) {
      setEditingMapping(mapping);
      setMappingFormData({
        businessCode: mapping.business_code,
        businessName: mapping.business_name || '',
        fullAccountNumber: mapping.full_account_number,
        fullRoutingNumber: mapping.full_routing_number,
        terminalIds: mapping.terminal_ids?.join(', ') || '',
        notes: mapping.notes || '',
        active: mapping.active !== false
      });
    } else {
      setEditingMapping(null);
      setMappingFormData({
        businessCode: '',
        businessName: '',
        fullAccountNumber: '',
        fullRoutingNumber: '',
        terminalIds: '',
        notes: '',
        active: true
      });
    }
    setMappingDialogOpen(true);
  };

  const handleCloseMappingDialog = () => {
    setMappingDialogOpen(false);
    setEditingMapping(null);
  };

  const handleSaveMapping = async () => {
    try {
      const payload = {
        ...mappingFormData,
        terminalIds: mappingFormData.terminalIds
          .split(',')
          .map(id => id.trim())
          .filter(id => id)
      };
      
      if (editingMapping) {
        await apiCall(`/api/v1/pai/account-mappings/${editingMapping.id}`, 'PUT', payload);
      } else {
        await apiCall('/api/v1/pai/account-mappings', 'POST', payload);
      }
      
      handleCloseMappingDialog();
      loadMappings();
      setError(null);
    } catch (error) {
      console.error('Failed to save PAI mapping:', error);
      setError(error.message || 'Failed to save PAI account mapping');
    }
  };

  const handleDeleteMapping = async (id) => {
    if (window.confirm('Are you sure you want to delete this mapping?')) {
      try {
        await apiCall(`/api/v1/pai/account-mappings/${id}`, 'DELETE');
        loadMappings();
        setError(null);
      } catch (error) {
        console.error('Failed to delete PAI mapping:', error);
        setError('Failed to delete PAI mapping');
      }
    }
  };

  const maskAccount = (account) => {
    if (!account || showAccountNumbers) {
      return account || '';
    }
    return `******${account.slice(-4)}`;
  };

  const maskRouting = (routing) => {
    if (!routing || showAccountNumbers) {
      return routing || '';
    }
    return `*****${routing.slice(-4)}`;
  };

  // Dashboard functions (from ATM Dashboard)
  const createLocalDate = (dateString) => {
    const parsed = parseISO(dateString);
    return parsed;
  };

  const fetchTerminalReports = async () => {
    setTerminalReportLoading(true);
    
    try {
      const dateStr = format(terminalReportDate, 'MM/dd/yyyy');
      
      // Fetch terminal status report
      const statusResponse = await apiCall('/api/v1/pai/terminal-reports', 'POST', {
        reportType: 'status',
        date: dateStr,
        account: 'Ross_Nolte'
      });
      
      if (statusResponse.data) {
        setTerminalStatusData(statusResponse.data);
      }
      
      // Fetch transaction details
      const transactionResponse = await apiCall('/api/v1/pai/terminal-reports', 'POST', {
        reportType: 'transactions',
        date: dateStr,
        account: 'Ross_Nolte'
      });
      
      if (transactionResponse.data) {
        setTerminalTransactionData(transactionResponse.data);
      }
    } catch (error) {
      console.error('Error fetching terminal reports:', error);
      setError('Failed to fetch terminal reports');
    } finally {
      setTerminalReportLoading(false);
    }
  };

  const fetchTrxLogDetails = async () => {
    setTrxLogLoading(true);
    setTrxLogData(null);
    
    try {
      const dateStr = format(terminalReportDate, 'MM/dd/yyyy');
      
      // Fetch detailed transaction logs
      const response = await apiCall('/api/v1/pai/terminal-reports', 'POST', {
        reportType: 'trxlog',
        date: dateStr,
        account: 'Ross_Nolte'
      });
      
      if (response.data) {
        setTrxLogData(response.data);
      }
    } catch (error) {
      console.error('Error fetching transaction logs:', error);
      setError('Failed to fetch transaction logs');
    } finally {
      setTrxLogLoading(false);
    }
  };

  const reconcileTransactions = async () => {
    setReconcileLoading(true);
    setReconcileData(null);
    
    try {
      const dateStr = format(terminalReportDate, 'MM/dd/yyyy');
      
      // Call reconcile endpoint
      const response = await apiCall('/api/v1/pai/reconcile', 'POST', {
        date: dateStr
      });
      
      console.log('Reconciliation response:', response);
      
      // Handle response whether it's response.data or direct response
      const data = response.data || response;
      setReconcileData(data);
      console.log('Reconciliation completed:', data);
    } catch (error) {
      console.error('Error reconciling transactions:', error);
      setError('Failed to reconcile transactions');
    } finally {
      setReconcileLoading(false);
    }
  };

  const importTrxLogToDatabase = async () => {
    setImportLoading(true);
    
    try {
      const dateStr = format(terminalReportDate, 'MM/dd/yyyy');
      
      // Import TrxLog data to pai_trxlog table (using download-report endpoint to avoid CORS)
      const response = await apiCall('/api/v1/pai/download-report', 'POST', {
        reportType: 'import-trxlog',
        date: dateStr,
        account: 'Ross_Nolte'
      });
      
      if (response.success) {
        let message = `Successfully imported ${response.insertedCount || 0} transactions for ${dateStr}`;
        if (response.skippedCount && response.skippedCount > 0) {
          message += ` (${response.skippedCount} duplicates skipped)`;
        }
        if (response.existingCount) {
          message = `Data already exists for ${dateStr} (${response.existingCount} transactions)`;
        }
        setSuccess(message);
        // Optionally refresh the TrxLog display
        if (trxLogData) {
          await fetchTrxLogDetails();
        }
      } else {
        setError(response.error || 'Failed to import transactions');
      }
    } catch (error) {
      console.error('Error importing transaction logs:', error);
      setError('Failed to import transaction logs');
    } finally {
      setImportLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    setDashboardLoading(true);

    try {
      let params = {};

      if (viewMode === 'single') {
        params.date = format(selectedDate, 'yyyy-MM-dd');
      } else {
        params.startDate = format(startDate, 'yyyy-MM-dd');
        params.endDate = format(endDate, 'yyyy-MM-dd');
      }

      // If impersonating, pass the impersonated user's businesses for filtering
      const isImpersonating = sessionStorage.getItem('isImpersonating') === 'true';
      if (isImpersonating) {
        const impersonatedUser = JSON.parse(sessionStorage.getItem('impersonatedUser') || '{}');
        if (impersonatedUser.allowedBusinesses && impersonatedUser.allowedBusinesses.length > 0) {
          params.impersonatedBusinesses = JSON.stringify(impersonatedUser.allowedBusinesses);
        }
        if (impersonatedUser.allowedTerminals && impersonatedUser.allowedTerminals.length > 0) {
          params.impersonatedTerminals = JSON.stringify(impersonatedUser.allowedTerminals);
        }
      }

      const response = await apiCall('/api/v1/atm/dashboard', 'GET', null, params);

      // Log what we received for date range
      if (viewMode === 'range' && response.topTerminals) {
        console.log('Date range response received:', {
          totalRecords: response.topTerminals.length,
          uniqueDates: [...new Set(response.topTerminals.map(t => t.transactionDate))].sort(),
          requestedRange: `${params.startDate} to ${params.endDate}`
        });
      }

      setDashboardData({
        dailyStats: response.dailyStats || dashboardData.dailyStats,
        responseBreakdown: response.responseBreakdown || [],
        topTerminals: response.topTerminals || [],
        availableDates: response.availableDates || [],
        summaryStats: response.summaryStats || dashboardData.summaryStats
      });

      // Extract unique locations from terminals data + PAI account mappings
      // Merge both sources so all PAI-mapped locations are always selectable
      const terminalLocations = (response.topTerminals || [])
        .map(t => t.location || t.businessName)
        .filter(l => l && l !== 'Unknown' && l !== '');
      const merged = [...new Set([...terminalLocations, ...paiLocations])].sort();
      if (merged.length > 0) {
        setAvailableLocations(merged);
      }
    } catch (err) {
      console.error('Error fetching PAI dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setDashboardLoading(false);
    }
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const handleExportCSV = async () => {
    try {
      console.log('PAI Dashboard Export - Version 4.0 with daily breakdown and location filter');

      const csvData = [];
      // Dynamic column headers based on selected types
      let headers = ['Date', 'Location', 'Route', 'Account', 'Approved Transactions'];
      if (selectedTypes.includes('Settlement')) {
        headers.push('Total Withdrawals');
      }
      if (selectedTypes.includes('Surcharge')) {
        headers.push('Total Surcharge');
      }

      // Add Settle Date and Bank Batch ID columns for date range exports
      if (viewMode === 'range') {
        headers.push('Settle Date', 'Bank Batch ID');
      }

      csvData.push(headers);
      setExportMenuAnchor(null);

      let totalApproved = 0;
      let totalWithdrawals = 0;
      let totalSurcharge = 0;

      console.log('Export mode:', viewMode);
      console.log('Selected locations:', selectedLocations);
      console.log('Selected types:', selectedTypes);

      // Filter terminals by selected locations
      let filteredTerminals = dashboardData.topTerminals;
      if (selectedLocations.length > 0) {
        filteredTerminals = dashboardData.topTerminals.filter(terminal =>
          selectedLocations.includes(terminal.location || terminal.businessName)
        );
      }

      // Helper function to process terminals for a single date - SHARED BY BOTH MODES
      const processSingleDate = (terminalsForDate, dateStr, shouldMask = false) => {
        // Group terminals by location/route/account combination
        const consolidatedData = {};

        terminalsForDate.forEach(terminal => {
          const locationName = terminal.location || terminal.businessName || 'Unknown';
          // Mask for date range (report), full numbers for daily (bank processing)
          const route = shouldMask && terminal.route ? `****${terminal.route.slice(-4)}` : (terminal.route || '');
          const account = shouldMask && terminal.account ? `****${terminal.account.slice(-4)}` : (terminal.account || '');

          // Use full numbers for grouping key to ensure proper consolidation
          const keyRoute = terminal.route || '';
          const keyAccount = terminal.account || '';
          const key = `${locationName}|${keyRoute}|${keyAccount}`;

          if (!consolidatedData[key]) {
            consolidatedData[key] = {
              locationName: locationName,
              route: route,  // This will be masked or full based on shouldMask
              account: account,  // This will be masked or full based on shouldMask
              approvedCount: 0,
              withdrawalAmount: 0,
              surchargeAmount: 0
            };
          }

          consolidatedData[key].approvedCount += terminal.approvedCount || 0;
          consolidatedData[key].withdrawalAmount += terminal.withdrawalAmount || 0;
          consolidatedData[key].surchargeAmount += terminal.surchargeAmount || 0;
        });

        // Sort by withdrawal amount descending and add to CSV
        Object.values(consolidatedData)
          .sort((a, b) => b.withdrawalAmount - a.withdrawalAmount)
          .forEach(row => {
            let rowData = [
              dateStr,
              row.locationName,
              row.route,
              row.account,
              row.approvedCount
            ];

            if (selectedTypes.includes('Settlement')) {
              rowData.push(Math.round(row.withdrawalAmount));
            }
            if (selectedTypes.includes('Surcharge')) {
              rowData.push(Math.round(row.surchargeAmount));
            }

            csvData.push(rowData);

            totalApproved += row.approvedCount;
            if (selectedTypes.includes('Settlement')) {
              totalWithdrawals += row.withdrawalAmount;
            }
            if (selectedTypes.includes('Surcharge')) {
              totalSurcharge += row.surchargeAmount;
            }
          });
      };

      // For date range mode, loop through each date
      if (viewMode === 'range') {
        // Group terminals by date first
        const terminalsByDate = {};
        filteredTerminals.forEach(terminal => {
          const date = terminal.transactionDate || format(new Date(), 'yyyy-MM-dd');
          if (!terminalsByDate[date]) {
            terminalsByDate[date] = [];
          }
          terminalsByDate[date].push(terminal);
        });

        // Sort dates and process each one - show each location as separate row like single day
        const sortedDates = Object.keys(terminalsByDate).sort();
        sortedDates.forEach(dateStr => {
          // For date range exports, we want each location as a separate row (like your Excel example)
          // NOT consolidated by location+route+account
          const terminalsForDate = terminalsByDate[dateStr];

          terminalsForDate.forEach(terminal => {
            const locationName = terminal.location || terminal.businessName || 'Unknown';

            // Get account info with masking for date range reports (same logic as processSingleDate)
            const shouldMask = true; // mask for date range reports
            const route = shouldMask && terminal.route ? `****${terminal.route.slice(-4)}` : (terminal.route || '');
            const account = shouldMask && terminal.account ? `****${terminal.account.slice(-4)}` : (terminal.account || '');

            let rowData = [
              dateStr,
              locationName,
              route,
              account,
              terminal.approvedCount || 0
            ];

            if (selectedTypes.includes('Settlement')) {
              rowData.push(Math.round(terminal.withdrawalAmount || 0));
            }
            if (selectedTypes.includes('Surcharge')) {
              rowData.push(Math.round(terminal.surchargeAmount || 0));
            }

            // Add Settle Date (same as transaction date) and Bank Batch ID for manual entry
            rowData.push(dateStr, ''); // Settle Date (transaction settlement date) and Bank Batch ID

            csvData.push(rowData);

            // Add to totals
            totalApproved += terminal.approvedCount || 0;
            if (selectedTypes.includes('Settlement')) {
              totalWithdrawals += terminal.withdrawalAmount || 0;
            }
            if (selectedTypes.includes('Surcharge')) {
              totalSurcharge += terminal.surchargeAmount || 0;
            }
          });
        });
      } else {
        // Single date mode - NO MASKING for bank processing
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        processSingleDate(filteredTerminals, dateStr, false);  // false = full numbers for bank
      }

      // Add total row with dynamic columns based on selected types
      csvData.push(['']);
      let totalRow = ['', 'TOTALS', '', '', totalApproved];
      if (selectedTypes.includes('Settlement')) {
        totalRow.push(Math.round(totalWithdrawals));
      }
      if (selectedTypes.includes('Surcharge')) {
        totalRow.push(Math.round(totalSurcharge));
      }

      // Add empty Settle Date and Bank Batch ID columns to totals row for date range exports
      if (viewMode === 'range') {
        totalRow.push('', ''); // Empty Settle Date and Bank Batch ID columns (no totals needed)
      }

      csvData.push(totalRow);

      const csvContent = csvData.map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);

      // Generate filename based on mode
      let filename;
      if (viewMode === 'range') {
        filename = `pai_daily_export_${format(startDate, 'yyyyMMdd')}_to_${format(endDate, 'yyyyMMdd')}.csv`;
      } else {
        filename = `pai_daily_export_${format(selectedDate, 'yyyyMMdd')}.csv`;
      }
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Export failed:', error);
      setError('Export failed: ' + error.message);
    } finally {
      setExportMenuAnchor(null);
    }
  };
  
  // NACHA export handler
  const handleExportNACHA = async () => {
    try {
      setExportMenuAnchor(null);
      setExportLoading(true);
      
      // Check if settings exist in localStorage
      const savedSettings = localStorage.getItem('nachaSettings');
      if (!savedSettings) {
        // Alert user to configure settings first
        setError('Please configure NACHA settings first. Go to the NACHA Settings tab.');
        setExportLoading(false);
        setTabValue('settings'); // NACHA settings live on the Settings tab (index 3 used to land on Manual Import)
        return;
      }
      
      const settings = JSON.parse(savedSettings);
      
      // For now, create a sample NACHA file locally
      // This will be replaced with backend API call when available
      const nachaContent = generateLocalNachaFile(settings, dashboardData);
      
      // Download the NACHA file
      const blob = new Blob([nachaContent], { type: 'text/plain;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const dateStr = format(selectedDate, 'yyyyMMdd');
      link.setAttribute('href', url);
      link.setAttribute('download', `ACH_${settings.companyName.replace(/\s+/g, '_')}_${dateStr}.txt`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('NACHA export completed locally');
      setError(null);
    } catch (error) {
      console.error('NACHA export failed:', error);
      setError('NACHA export failed: ' + error.message);
    } finally {
      setExportLoading(false);
    }
  };
  
  // Generate NACHA file locally (temporary until backend is ready)
  const generateLocalNachaFile = (settings, data) => {
    const now = new Date();
    const dateStr = format(now, 'yyMMdd');
    const timeStr = format(now, 'HHmm');
    
    // File Header Record
    let nacha = '101';
    nacha += ` ${settings.immediateDestination}`.padEnd(10);
    nacha += ` ${settings.immediateOrigin}`.padEnd(10);
    nacha += dateStr;
    nacha += timeStr;
    nacha += settings.fileIdModifier;
    nacha += '094';
    nacha += '10';
    nacha += '1';
    nacha += settings.immediateDestinationName.padEnd(23);
    nacha += settings.immediateOriginName.padEnd(23);
    nacha += '        \n'; // Reference code
    
    // Batch Header Record
    nacha += '5';
    nacha += settings.serviceClassCode;
    nacha += settings.companyName.padEnd(16);
    nacha += settings.companyDiscretionaryData.padEnd(20);
    nacha += settings.companyIdentification.padEnd(10);
    nacha += settings.standardEntryClass;
    nacha += settings.companyEntryDescription.padEnd(10);
    nacha += format(now, 'yyMMdd'); // Company descriptive date
    nacha += format(addDays(now, 1), 'yyMMdd'); // Effective entry date
    nacha += '   '; // Settlement date (blank)
    nacha += '1';
    nacha += settings.originatingDFIIdentification.substring(0, 8);
    nacha += '0000001\n'; // Batch number
    
    // Add sample entry detail records (would be populated from actual data)
    let entryCount = 0;
    let totalAmount = 0;
    
    // This is where you'd loop through actual payment data
    // For now, just a placeholder comment
    nacha += '// Entry detail records would go here\n';
    
    // Batch Control Record
    nacha += '8';
    nacha += settings.serviceClassCode;
    nacha += String(entryCount).padStart(6, '0');
    nacha += '0000000000'; // Entry hash
    nacha += String(totalAmount * 100).padStart(12, '0'); // Total debit amount in cents
    nacha += '000000000000'; // Total credit amount
    nacha += settings.companyIdentification.padEnd(10);
    nacha += '                         '; // Message authentication code
    nacha += '      '; // Reserved
    nacha += settings.originatingDFIIdentification.substring(0, 8);
    nacha += '0000001\n'; // Batch number
    
    // File Control Record
    nacha += '9';
    nacha += '000001'; // Batch count
    nacha += '000000'; // Block count
    nacha += String(entryCount).padStart(8, '0');
    nacha += '0000000000'; // Entry hash
    nacha += String(totalAmount * 100).padStart(12, '0'); // Total debit amount
    nacha += '000000000000'; // Total credit amount
    nacha += '                                       \n'; // Reserved
    
    return nacha;
  };
  
  // Import helper
  const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };
  
  // Handle NACHA settings save
  const handleNachaSettingsSave = async (settings) => {
    try {
      await saveNachaSettings(settings);
      setNachaSettings(settings);
      setNachaSettingsOpen(false);
      
      // Now export with the saved settings
      await handleExportNACHA();
    } catch (error) {
      console.error('Failed to save NACHA settings:', error);
      setError('Failed to save NACHA settings: ' + error.message);
    }
  };

  // Silently return null if no permission - prevents error from showing for users who shouldn't see PAI
  // Fetch PAI locations for reconciliation
  // Excluded locations that shouldn't appear in PAI settlement
  const excludedPaiLocations = ['Todd', 'Test', 'GreenWave', 'JFK'];

  const fetchPaiLocations = async () => {
    try {
      // Get unique locations from PAI account mappings
      const response = await apiCall('/api/v1/pai/account-mappings', 'GET');
      if (response && response.accounts) {
        const uniqueLocations = [...new Set(response.accounts.map(m => m.location_name).filter(l => l))]
          .filter(loc => !excludedPaiLocations.some(excluded => loc.toLowerCase().includes(excluded.toLowerCase())))
          .sort();
        setPaiLocations(uniqueLocations);
        console.log('Loaded PAI locations:', uniqueLocations);
      }
    } catch (error) {
      console.error('Error fetching PAI locations:', error);
      // If no mappings, try to get locations from businesses endpoint
      try {
        const businessResponse = await apiCall('/api/v1/businesses', 'GET');
        if (businessResponse && businessResponse.businesses) {
          // Filter for PAI locations (those with HG or GH terminals)
          const paiBusinesses = businessResponse.businesses.filter(b =>
            b.LocationName && (b.LocationName.includes('Rise') || b.LocationName.includes('Trulieve') || b.LocationName.includes('PAI'))
          );
          const uniqueLocations = [...new Set(paiBusinesses.map(b => b.LocationName))]
            .filter(loc => !excludedPaiLocations.some(excluded => loc.toLowerCase().includes(excluded.toLowerCase())))
            .sort();
          setPaiLocations(uniqueLocations);
          console.log('Loaded PAI locations from businesses:', uniqueLocations);
        }
      } catch (err) {
        console.error('Error fetching businesses:', err);
      }
    }
  };

  // Fetch all locations from all data sources (PAI mappings + settlement_deposits)
  const fetchAllLocations = async () => {
    try {
      const response = await apiCall('/api/v1/settlements/locations', 'GET');
      if (response && response.locations) {
        setAllLocations(response.locations);
        console.log('Loaded all locations:', response.locations);
      }
    } catch (error) {
      console.error('Error fetching all locations:', error);
      // Fallback to PAI locations
      if (paiLocations.length > 0) {
        setAllLocations(paiLocations.map(name => ({ location_name: name, processor: 'PAI' })));
      }
    }
  };

  // Location name normalization is now handled by the backend using pai_account_mappings
  // The backend looks up the canonical location name based on terminal ID
  const normalizeLocationName = (name) => {
    return name; // Pass through - backend will resolve using terminal ID lookup
  };

  // Parse DNS CSV file
  const parseCSVFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const lines = text.split('\n');
          const records = [];

          // Skip header row
          for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;

            // Parse CSV line (handle quoted values)
            const values = line.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g) || [];
            const cleanValues = values.map(v => v.replace(/^"|"$/g, '').trim());

            // Check if this is a data row (has settlement date and terminal ID)
            const settlementDate = cleanValues[3]; // SettlementDate column
            const terminalId = cleanValues[4]; // TerminalID column
            const fundsType = cleanValues[6]; // FundsType column
            const amount = cleanValues[7]; // Amount column
            const locationName = cleanValues[5]; // LocationName column

            // Skip rows without proper data (summary rows, empty rows)
            if (settlementDate && terminalId && fundsType && amount && terminalId.startsWith('DNS')) {
              // Parse date (format: "11/25/2025 12:00:00 AM")
              const dateParts = settlementDate.split(' ')[0].split('/');
              if (dateParts.length === 3) {
                const formattedDate = `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;

                // Pass through the actual funds type - backend will route ADJC/ADJD to dns_adjustments
                // SETL = Settlement, SRCH = Surcharge, ADJC = Interchange, ADJD = Buy Rate
                const depositType = ['SETL', 'SRCH', 'ADJC', 'ADJD'].includes(fundsType) ? fundsType : 'SETL';

                // Get additional fields for ADJC/ADJD records
                const aba = cleanValues[8] || null;        // ABA column
                const account = cleanValues[9] || null;    // Account column
                const accountType = cleanValues[10] || null; // AccountType column
                const batchHeader = cleanValues[11] || null; // BatchHeader column
                const notes = cleanValues[12] || null;     // Notes column (contains "Interchange" or "buyrate")
                const accountId = cleanValues[1] || null;  // AccountID column
                const holderName = cleanValues[2] || null; // HolderName column

                records.push({
                  terminal_id: terminalId,
                  location_name: normalizeLocationName(locationName),
                  original_location: locationName,
                  settlement_date: formattedDate,
                  deposit_type: depositType,
                  amount: parseFloat(amount) || 0,
                  account_holder: holderName || 'Prestige Payment',
                  aba: aba,
                  account: account,
                  account_type: accountType,
                  batch_header: batchHeader,
                  notes: notes,
                  account_id: accountId
                });
              }
            }
          }

          resolve(records);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  // Parse Switch Commerce XLSX file
  const parseSwitchCommerceXLSX = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          const records = [];
          let currentBusinessDate = null;
          let currentTerminal = null;
          let currentLocation = null;

          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (!row || row.length === 0) continue;

            const col1 = row[1] ? String(row[1]).trim() : '';
            const col2 = row[2] ? String(row[2]).trim() : '';
            const col11 = row[11]; // Vault Cash Amount
            const col14 = row[14]; // Other Amount

            // Check for Business Date row
            if (col1.startsWith('Business Date:')) {
              const dateMatch = col1.match(/(\d{2})\/(\d{2})\/(\d{4})/);
              if (dateMatch) {
                currentBusinessDate = `${dateMatch[3]}-${dateMatch[1]}-${dateMatch[2]}`;
              }
              continue;
            }

            // Check for Terminal row (NH terminal IDs)
            if (col1.startsWith('NH') && col1.length <= 10 && !col1.includes(' ')) {
              currentTerminal = col1;
              currentLocation = col2 || currentLocation;
              continue;
            }

            // Check for data rows (CASH or SCHG transactions)
            if (col1.includes('CASH') && currentTerminal && currentBusinessDate) {
              // Settlement row - amount is in Vault Cash Amount column
              const amount = parseFloat(col11) || 0;
              if (amount > 0) {
                records.push({
                  terminal_id: currentTerminal,
                  location_name: normalizeLocationName(currentLocation),
                  original_location: currentLocation,
                  settlement_date: currentBusinessDate,
                  deposit_type: 'SETL',
                  amount: amount,
                  account_holder: 'Switch Commerce'
                });
              }
            } else if (col1.includes('SCHG') && currentTerminal && currentBusinessDate) {
              // Surcharge row - amount is in Other Amount column
              const amount = parseFloat(col14) || 0;
              if (amount > 0) {
                records.push({
                  terminal_id: currentTerminal,
                  location_name: normalizeLocationName(currentLocation),
                  original_location: currentLocation,
                  settlement_date: currentBusinessDate,
                  deposit_type: 'SRCH',
                  amount: amount,
                  account_holder: 'Switch Commerce'
                });
              }
            }
          }

          console.log(`Parsed ${records.length} records from Switch Commerce XLSX`);
          resolve(records);
        } catch (err) {
          console.error('Error parsing XLSX:', err);
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  };

  // Parse Processor TransactionDetail XLSX file
  const parseProcessorTransactionDetailXLSX = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          // Don't use cellDates: true - we'll parse dates manually to avoid timezone issues
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null, raw: true });

          const records = [];
          let currentTerminal = null;
          let currentLocation = null;
          let inDataSection = false;

          // Helper to convert Excel serial date to JS Date
          const excelSerialToDate = (serial) => {
            if (serial instanceof Date) return serial;
            if (typeof serial !== 'number') return null;
            return new Date((serial - 25569) * 86400000);
          };

          // Helper to parse various date formats
          const parseDateValue = (value) => {
            if (value instanceof Date) return value;
            if (typeof value === 'number') return excelSerialToDate(value);
            if (typeof value === 'string') {
              const parts = value.split('/');
              if (parts.length === 3) return new Date(parts[2], parts[0] - 1, parts[1]);
              const d = new Date(value);
              return isNaN(d.getTime()) ? null : d;
            }
            return null;
          };

          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (!row || row.length === 0) continue;

            const firstCell = row[0];
            if (!firstCell) continue;

            const firstCellStr = String(firstCell).trim();

            // Skip totals rows first (before terminal check)
            if (firstCellStr.includes('Totals')) {
              inDataSection = false;
              continue;
            }

            // Check for terminal header row (e.g., "NH169704 R Niles")
            if (firstCellStr.match(/^NH\d+/)) {
              const parts = firstCellStr.split(' ');
              currentTerminal = parts[0];
              currentLocation = parts.slice(1).join(' ');
              inDataSection = false;
              console.log(`Found terminal: ${currentTerminal} - ${currentLocation}`);
              continue;
            }

            // Check for column headers row - this starts the data section
            if (firstCellStr === 'Calendar Date') {
              inDataSection = true;
              continue;
            }

            // Process data row
            if (inDataSection && currentTerminal) {
              // Expected columns:
              // 0: Calendar Date, 1: Transaction Date (datetime), 2: Transaction Type,
              // 5: Pan, 10: Dispensed Amount, 11: Fee Amount
              const calendarDate = row[0];
              const transactionDatetime = row[1];
              const transactionType = row[2];
              const panLastFour = row[5] ? String(row[5]).padStart(4, '0') : '';
              const dispensedAmount = parseFloat(row[10]) || 0;
              const surchargeAmount = parseFloat(row[11]) || 0;

              // Parse dates
              const parsedCalendarDate = parseDateValue(calendarDate);
              const parsedTransactionDatetime = parseDateValue(transactionDatetime);

              // Validate required fields
              if (!parsedTransactionDatetime || !parsedCalendarDate) {
                continue;
              }

              // Include all transaction types with positive dispensed amount
              if (dispensedAmount > 0) {
                const settlementDate = format(parsedCalendarDate, 'yyyy-MM-dd');
                records.push({
                  terminal_id: currentTerminal,
                  location_name: normalizeLocationName(currentLocation),
                  original_location: currentLocation,
                  settlement_date: settlementDate,
                  deposit_type: 'SETL',
                  amount: dispensedAmount,
                  surcharge_amount: surchargeAmount,
                  card_last_four: panLastFour,
                  transaction_datetime: parsedTransactionDatetime.toISOString(),
                  transaction_type: transactionType,
                  account_holder: 'Processor TransactionDetail'
                });
              }
            }
          }

          console.log(`Parsed ${records.length} records from Processor TransactionDetail XLSX`);
          resolve(records);
        } catch (err) {
          console.error('Error parsing TransactionDetail XLSX:', err);
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  };

  // Detect if XLSX file is TransactionDetail format
  const isTransactionDetailFormat = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          // Check for TransactionDetail indicators
          for (let i = 0; i < Math.min(jsonData.length, 30); i++) {
            const row = jsonData[i];
            if (!row || row.length === 0) continue;
            const col0 = row[0] ? String(row[0]).trim() : '';

            // TransactionDetail has "Calendar Date" as column header
            if (col0 === 'Calendar Date') {
              resolve(true);
              return;
            }
            // Or has terminal pattern followed by location name (NH169704 R Niles)
            if (/^NH\d+\s+\w/.test(col0)) {
              resolve(true);
              return;
            }
          }
          resolve(false);
        } catch {
          resolve(false);
        }
      };
      reader.onerror = () => resolve(false);
      reader.readAsArrayBuffer(file);
    });
  };

  // Handle file selection for import
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImportFile(file);
    setImportParsing(true);
    setImportPreview(null);

    try {
      let records;
      const fileName = file.name.toLowerCase();

      // Detect file type and use appropriate parser
      if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        // Check if it's a TransactionDetail format
        const isTransDetail = await isTransactionDetailFormat(file);
        if (isTransDetail) {
          // Processor TransactionDetail format
          records = await parseProcessorTransactionDetailXLSX(file);
          setImportProcessor('PROCESSOR');
        } else {
          // Switch Commerce XLSX format
          records = await parseSwitchCommerceXLSX(file);
          setImportProcessor('SwitchCommerce');
        }
      } else {
        // DNS CSV format
        records = await parseCSVFile(file);
        // Auto-select DNS processor for CSV files
        setImportProcessor('DNS');
      }

      // Group by date for summary
      const dateGroups = {};
      records.forEach(r => {
        if (!dateGroups[r.settlement_date]) {
          dateGroups[r.settlement_date] = { settlements: 0, surcharges: 0, total: 0 };
        }
        if (r.deposit_type === 'SETL') {
          dateGroups[r.settlement_date].settlements += r.amount;
        } else {
          dateGroups[r.settlement_date].surcharges += r.amount;
        }
        dateGroups[r.settlement_date].total += r.amount;
      });

      setImportPreview({
        records,
        totalRecords: records.length,
        dates: Object.keys(dateGroups).sort(),
        dateGroups,
        terminals: [...new Set(records.map(r => r.terminal_id))],
        locations: [...new Set(records.map(r => r.location_name))]
      });
    } catch (err) {
      setError('Failed to parse file: ' + err.message);
    } finally {
      setImportParsing(false);
    }
  };

  // Submit import
  const handleImportSubmit = async () => {
    if (!importPreview || !importPreview.records.length) return;

    setImportSubmitting(true);
    try {
      let response;
      let successMessage;

      if (importProcessor === 'PROCESSOR') {
        // For Processor TransactionDetail, use the processor-transactions endpoint
        response = await apiCall('/api/v1/processor-transactions/import', 'POST', {
          records: importPreview.records
        });
        successMessage = `Imported ${response.inserted || 0} transactions. Matched: ${response.matching?.matched || 0}, Unmatched: ${response.matching?.unmatched || 0}`;
      } else {
        // For DNS/Switch Commerce, use the settlements import endpoint
        response = await apiCall('/api/v1/settlements/import', 'POST', {
          processor: importProcessor,
          records: importPreview.records
        });
        successMessage = `Successfully imported ${response.inserted || importPreview.records.length} records`;
      }

      setSuccess(successMessage);
      setImportFile(null);
      setImportPreview(null);

      // Add to history
      setImportHistory(prev => [{
        date: new Date().toISOString(),
        filename: importFile.name,
        records: importPreview.totalRecords,
        processor: importProcessor
      }, ...prev.slice(0, 9)]);

      // Refresh locations
      fetchAllLocations();
    } catch (err) {
      setError('Import failed: ' + err.message);
    } finally {
      setImportSubmitting(false);
    }
  };

  // Fetch settlement report for multiple locations
  const fetchSettlementReport = async () => {
    if (!settlementLocations || settlementLocations.length === 0) return;

    setSettlementLoading(true);
    try {
      // Fetch data for all selected locations in parallel
      const promises = settlementLocations.map(loc =>
        apiCall('/api/v1/settlements/report', 'GET', null, {
          location: loc.location_name,
          startDate: format(settlementStartDate, 'yyyy-MM-dd'),
          endDate: format(settlementEndDate, 'yyyy-MM-dd'),
          dayType: settlementDayType // 'processor' (3pm cutoff) or 'calendar' (midnight-midnight)
        })
      );

      const results = await Promise.all(promises);

      // Combine results from all locations
      let allRecords = [];
      let totalSettlement = 0;
      let totalSurcharge = 0;

      results.forEach((response, idx) => {
        if (response.records) {
          // Add location info to each record for display
          const recordsWithLocation = response.records.map(r => ({
            ...r,
            location_name: settlementLocations[idx].location_name
          }));
          allRecords = [...allRecords, ...recordsWithLocation];
        }
        totalSettlement += parseFloat(response.totals?.settlement || 0);
        totalSurcharge += parseFloat(response.totals?.surcharge || 0);
      });

      // Sort by date descending, then by location
      allRecords.sort((a, b) => {
        const dateCompare = new Date(b.settlement_date) - new Date(a.settlement_date);
        if (dateCompare !== 0) return dateCompare;
        return (a.location_name || '').localeCompare(b.location_name || '');
      });

      setSettlementData({
        records: allRecords,
        totalSettlement,
        totalSurcharge,
        totalRecords: allRecords.length,
        source: settlementLocations.length > 1 ? 'Multiple' : (allRecords[0]?.source_type || 'DNS'),
        locationCount: settlementLocations.length
      });
    } catch (err) {
      setError('Failed to fetch settlement report: ' + err.message);
    } finally {
      setSettlementLoading(false);
    }
  };

  // Settlement-by-processor (admin-only). ONE call returns every location that
  // reconciled in the date range for the chosen processor. Single source of truth =
  // settlement_batches (RECONCILED), calendar day (12am-12am). No location input.
  const fetchSettlementByProcessor = async () => {
    if (!settlementProcessor) return;

    setSettlementLoading(true);
    setError(null);
    try {
      const response = await apiCall('/api/v1/settlements/report', 'GET', null, {
        byProcessor: settlementProcessor,
        startDate: format(settlementStartDate, 'yyyy-MM-dd'),
        endDate: format(settlementEndDate, 'yyyy-MM-dd')
      });

      const records = (response.records || []).sort((a, b) => {
        const dateCompare = new Date(b.settlement_date) - new Date(a.settlement_date);
        if (dateCompare !== 0) return dateCompare;
        return (a.location_name || '').localeCompare(b.location_name || '');
      });

      setSettlementData({
        records,
        totalSettlement: parseFloat(response.totals?.settlement || 0),
        totalSurcharge: parseFloat(response.totals?.surcharge || 0),
        totalRecords: records.length,
        source: 'RECONCILED',
        locationCount: response.locationCount || 0,
        // SPLITS basis (2026-07-08): SRCH records are per-payee split lines computed from
        // settlement_transactions × PAI surcharge accounts; achSurchargeExport is one payment
        // per unique route+account.
        srchBasis: response.srchBasis || null,
        achSurchargeExport: response.achSurchargeExport || [],
        // Per-business-day ACH send status from the ach_transmissions ledger:
        // which vault file the day went out in, or NOT_SENT. (Automated send began 8/11/2026.)
        achStatus: response.achStatus || [],
        // Surcharge is MONTHLY: one SRCH file per range (ledger keyed on range end date).
        achSrchStatus: response.achSrchStatus || null
      });
    } catch (err) {
      setSettlementData(null);
      setError('Failed to load settlement: ' + (err.message || 'unknown error'));
    } finally {
      setSettlementLoading(false);
    }
  };

  // Run Settlement - selects all PAI locations and runs settlement report
  const handleRunPaiSettlement = async () => {
    // Validate at least one type is selected
    if (!runSettlementTypes.settlement && !runSettlementTypes.surcharge) {
      setError('Please select at least Settlement or Surcharge');
      return;
    }

    // Convert paiLocations (array of strings) to format expected by settlementLocations
    const paiLocationObjects = paiLocations.map(locName => ({
      location_name: locName,
      processors: ['PAI']
    }));

    // Set the locations and close dialog
    setSettlementLocations(paiLocationObjects);
    setRunSettlementOpen(false);

    // Use the selected date for both start and end (single day settlement)
    const dateStr = format(runSettlementDate, 'yyyy-MM-dd');

    // Run the settlement report after state update
    setSettlementLoading(true);
    try {
      // Fetch data for all PAI locations in parallel
      const promises = paiLocationObjects.map(loc =>
        apiCall('/api/v1/settlements/report', 'GET', null, {
          location: loc.location_name,
          startDate: dateStr,
          endDate: dateStr,
          dayType: runSettlementCutoff
        })
      );

      const results = await Promise.all(promises);

      // Combine results from all locations
      let allRecords = [];
      let totalSettlement = 0;
      let totalSurcharge = 0;

      results.forEach((response, idx) => {
        if (response.records) {
          // Filter records based on selected types
          const filteredRecords = response.records.filter(r => {
            if (r.deposit_type === 'SETL' && runSettlementTypes.settlement) return true;
            if (r.deposit_type === 'SRCH' && runSettlementTypes.surcharge) return true;
            return false;
          });

          const recordsWithLocation = filteredRecords.map(r => ({
            ...r,
            location_name: paiLocationObjects[idx].location_name
          }));
          allRecords = [...allRecords, ...recordsWithLocation];
        }
        if (runSettlementTypes.settlement) {
          totalSettlement += parseFloat(response.totals?.settlement || 0);
        }
        if (runSettlementTypes.surcharge) {
          totalSurcharge += parseFloat(response.totals?.surcharge || 0);
        }
      });

      // Sort by date descending, then by location
      allRecords.sort((a, b) => {
        const dateCompare = new Date(b.settlement_date) - new Date(a.settlement_date);
        if (dateCompare !== 0) return dateCompare;
        return (a.location_name || '').localeCompare(b.location_name || '');
      });

      // Build type description
      const types = [];
      if (runSettlementTypes.settlement) types.push('Settlement');
      if (runSettlementTypes.surcharge) types.push('Surcharge');

      setSettlementData({
        records: allRecords,
        totalSettlement,
        totalSurcharge,
        totalRecords: allRecords.length,
        source: 'PAI',
        locationCount: paiLocationObjects.length
      });

      // Auto-download CSV file
      if (allRecords.length > 0) {
        // Filter to only SETL (settlement/withdrawal) records for ACH export
        const settlementRecords = allRecords.filter(r => r.deposit_type === 'SETL');

        if (settlementRecords.length > 0) {
          // Aggregate by date + location + route + account
          const aggregated = {};
          let csvTotalTransactions = 0;
          let csvTotalWithdrawals = 0;

          settlementRecords.forEach(record => {
            const recordDateStr = record.settlement_date
              ? record.settlement_date.substring(0, 10)
              : format(runSettlementDate, 'yyyy-MM-dd');
            const location = record.location_name || '';
            const route = record.route || '';
            const account = record.account || '';

            const key = `${recordDateStr}|${location}|${route}|${account}`;

            if (!aggregated[key]) {
              aggregated[key] = {
                date: recordDateStr,
                location,
                route,
                account,
                transactions: 0,
                withdrawals: 0
              };
            }

            aggregated[key].transactions += parseInt(record.transaction_count) || 0;
            aggregated[key].withdrawals += parseFloat(record.amount) || 0;
            csvTotalTransactions += parseInt(record.transaction_count) || 0;
            csvTotalWithdrawals += parseFloat(record.amount) || 0;
          });

          // Convert to array and sort by date desc, then location
          const rows = Object.values(aggregated).sort((a, b) => {
            const dateCompare = b.date.localeCompare(a.date);
            if (dateCompare !== 0) return dateCompare;
            return a.location.localeCompare(b.location);
          });

          // Build CSV in ACH format
          const headers = ['Date', 'Location', 'Route', 'Account', 'Approved Transactions', 'Total Withdrawals'];

          const csvRows = rows.map(row => [
            row.date,
            row.location,
            row.route,
            row.account,
            row.transactions,
            Math.round(row.withdrawals)
          ]);

          // Add blank row and totals
          csvRows.push(['', '', '', '', '', '']);
          csvRows.push(['', 'TOTALS', '', '', csvTotalTransactions, Math.round(csvTotalWithdrawals)]);

          // Create CSV content
          const csvContent = [
            headers.join(','),
            ...csvRows.map(row => row.map(cell => {
              const cellStr = String(cell);
              if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                return `"${cellStr.replace(/"/g, '""')}"`;
              }
              return cellStr;
            }).join(','))
          ].join('\n');

          // Create blob and download
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;

          // Build filename with selected date
          const exportDateStr = format(runSettlementDate, 'yyyyMMdd');
          link.download = `pai_settlement_${exportDateStr}.csv`;

          // Trigger download
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      }

      setSuccess(`${types.join(' & ')} report generated for ${paiLocations.length} locations on ${format(runSettlementDate, 'MMM d, yyyy')}`);
    } catch (err) {
      setError('Failed to fetch settlement report: ' + err.message);
    } finally {
      setSettlementLoading(false);
    }
  };

  // Load the ACH transmission ledger (ACH Management tab)
  const loadAchHistory = async () => {
    setAchLoading(true);
    try {
      const response = await apiCall('/api/v1/settlements/report', 'GET', null, { achHistory: 'true' });
      setAchTransmissions(response.transmissions || []);
    } catch (err) {
      setError('Failed to load ACH history: ' + (err.message || 'unknown error'));
    } finally {
      setAchLoading(false);
    }
  };

  // Generate the surcharge NACHA file (one file for the whole range) for review download.
  const handleGenerateNachaSrch = async () => {
    if (!settlementProcessor) return;
    setSettlementLoading(true);
    try {
      const response = await apiCall('/api/v1/settlements/report', 'GET', null, {
        byProcessor: settlementProcessor,
        startDate: format(settlementStartDate, 'yyyy-MM-dd'),
        endDate: format(settlementEndDate, 'yyyy-MM-dd'),
        format: 'nacha', nachaType: 'SRCH',
        ...(achEffectiveDate ? { effectiveDate: achEffectiveDate } : {})
      });
      const f = (response.files || [])[0];
      if (!f) { setError('No surcharge splits in range — nothing to generate'); return; }
      if (!f.validation || !f.validation.valid) { setError(`SRCH validation failed — not downloaded`); return; }
      const blob = new Blob([f.content], { type: 'text/plain;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = f.fileName;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setSuccess(`Generated ${f.fileName} — ${f.entryCount} payees, $${f.totalCredits}, effective ${response.effectiveDate}`);
    } catch (err) {
      setError('SRCH generation failed: ' + (err.message || 'unknown error'));
    } finally {
      setSettlementLoading(false);
    }
  };

  // Send the surcharge NACHA file to the bank SFTP (archive → ledger → send).
  const handleSendSurchargeACH = async () => {
    if (!settlementProcessor) return;
    const range = `${format(settlementStartDate, 'MM/dd/yyyy')}–${format(settlementEndDate, 'MM/dd/yyyy')}`;
    if (!window.confirm(`Send the SURCHARGE ACH file for ${range} to the bank SFTP?\n\nOne RMT055LS<date>_S file covering the whole range. Review it first with "Generate NACHA (SRCH)".`)) return;
    setAchSending(true);
    try {
      const response = await apiCall('/api/v1/settlements/report', 'GET', null, {
        byProcessor: settlementProcessor,
        startDate: format(settlementStartDate, 'yyyy-MM-dd'),
        endDate: format(settlementEndDate, 'yyyy-MM-dd'),
        format: 'nacha', nachaType: 'SRCH', send: 'true',
        ...(achEffectiveDate ? { effectiveDate: achEffectiveDate } : {})
      });
      const r = (response.results || [])[0] || {};
      if (r.status === 'SENT') setSuccess(`Surcharge ACH sent: ${r.fileName} → ${r.remotePath || 'bank SFTP'}`);
      else if (r.status === 'alreadySent') setError(`Surcharge for this range was already sent (transmission #${r.transmissionId})`);
      else setError(`Surcharge ACH failed: ${r.error || 'unknown error'} — see the ACH Management tab`);
      loadAchHistory();
    } catch (err) {
      setError('Surcharge ACH send failed: ' + (err.message || 'unknown error'));
    } finally {
      setAchSending(false);
    }
  };

  // Load the ledger whenever the ACH Management tab is opened
  useEffect(() => {
    if (tabValue === 'ach') loadAchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  // Resend a FAILED ACH file — re-pushes the SAME archived S3 file to the bank SFTP.
  const handleAchResend = async (transmissionId, fileName) => {
    if (!window.confirm(`Resend ${fileName} to the bank SFTP?\n\nThis re-transmits the exact file that was already generated (same trace numbers).`)) {
      return;
    }
    setAchSending(true);
    try {
      const response = await apiCall('/api/v1/settlements/report', 'GET', null, { achResend: String(transmissionId) });
      if (response.status === 'SENT') {
        setSuccess(`${fileName} resent successfully → ${response.remotePath || 'bank SFTP'}`);
      } else {
        setError(`Resend of ${fileName} failed: ${response.error || 'unknown error'}`);
      }
    } catch (err) {
      setError(`Resend of ${fileName} failed: ${err.message || 'unknown error'}`);
    } finally {
      setAchSending(false);
      loadAchHistory();
    }
  };

  // Download an archived ACH file via a presigned S3 link
  const handleAchDownload = async (transmissionId) => {
    try {
      const response = await apiCall('/api/v1/settlements/report', 'GET', null, { achDownload: String(transmissionId) });
      if (response.url) window.open(response.url, '_blank');
    } catch (err) {
      setError('Failed to get download link: ' + (err.message || 'unknown error'));
    }
  };

  // Send ACH — generates the NACHA file(s) for the selected range, archives each to S3,
  // records it in the ledger, and pushes to the bank SFTP over the ACH VPN. Results
  // (SENT acknowledgement / FAILED with reason / alreadySent guard) land on the
  // ACH Management tab.
  const handleSendACH = async () => {
    if (!settlementProcessor) return;
    const range = `${format(settlementStartDate, 'MM/dd/yyyy')}–${format(settlementEndDate, 'MM/dd/yyyy')}`;
    if (!window.confirm(`Send settlement ACH file(s) for ${range} to the bank SFTP?\n\nEach business day in the range becomes one RMT055LS<date>_V file. Days already sent are skipped automatically.`)) {
      return;
    }
    setAchSending(true);
    try {
      const response = await apiCall('/api/v1/settlements/report', 'GET', null, {
        byProcessor: settlementProcessor,
        startDate: format(settlementStartDate, 'yyyy-MM-dd'),
        endDate: format(settlementEndDate, 'yyyy-MM-dd'),
        format: 'nacha',
        send: 'true',
        ...(achEffectiveDate ? { effectiveDate: achEffectiveDate } : {})
      });
      const r = response.results || [];
      const summary = `ACH send: ${response.sent} sent, ${response.failed} failed, ${response.skipped} already sent` +
        (r.length ? ` — ${r.map(x => `${x.fileName}: ${x.status}`).join('; ')}` : '');
      if (response.failed > 0) setError(summary + ' — see the ACH Management tab for details');
      else setSuccess(summary);
      loadAchHistory();
    } catch (err) {
      setError('ACH send failed: ' + (err.message || 'unknown error'));
    } finally {
      setAchSending(false);
    }
  };

  // RESEND a file the bank already received but failed to process: re-pushes the SAME
  // archived bytes (same traces) via ?achResend=<id>&force=true. Requires typing CONFIRM.
  const handleResendACH = async () => {
    if (resendConfirmText !== 'CONFIRM' || !resendTxId) return;
    setAchSending(true);
    setResendDialogOpen(false);
    try {
      const response = await apiCall('/api/v1/settlements/report', 'GET', null, {
        achResend: String(resendTxId),
        force: 'true',
        ...(achEffectiveDate ? { effectiveDate: achEffectiveDate } : {})
      });
      if (response.status === 'SENT') {
        setSuccess(`RESENT ${response.fileName} — status SENT (${response.remotePath || 'bank SFTP'})`);
      } else {
        setError(`Resend of ${response.fileName || 'file'} FAILED: ${response.error || 'unknown error'}`);
      }
      loadAchHistory();
      fetchSettlementByProcessor();
    } catch (err) {
      setError('ACH resend failed: ' + (err.message || 'unknown error'));
    } finally {
      setAchSending(false);
      setResendTxId('');
      setResendConfirmText('');
    }
  };

  // Generate NACHA settlement (SET) files — one bank-ready file per business day in the
  // selected range, named RMT055LS<MMDDYYYY>_V.txt. Format proven byte-identical to the
  // bank-accepted stream; trace numbers/file modifiers are reserved server-side.
  const handleGenerateNacha = async () => {
    if (!settlementProcessor) return;
    setSettlementLoading(true);
    try {
      const response = await apiCall('/api/v1/settlements/report', 'GET', null, {
        byProcessor: settlementProcessor,
        startDate: format(settlementStartDate, 'yyyy-MM-dd'),
        endDate: format(settlementEndDate, 'yyyy-MM-dd'),
        format: 'nacha',
        ...(achEffectiveDate ? { effectiveDate: achEffectiveDate } : {})
      });
      const files = response.files || [];
      if (files.length === 0) {
        setError('No reconciled settlement days in the selected range — nothing to generate');
        return;
      }
      const invalid = files.filter(f => !f.validation || !f.validation.valid);
      if (invalid.length > 0) {
        setError(`NACHA validation failed for: ${invalid.map(f => f.fileName).join(', ')} — files NOT downloaded`);
        return;
      }
      files.forEach(f => {
        const blob = new Blob([f.content], { type: 'text/plain;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = f.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      });
      setSuccess(`Generated ${files.length} NACHA file(s), effective ${response.effectiveDate}: ${files.map(f => `${f.fileName} ($${f.totalCredits})`).join(', ')}`);
    } catch (err) {
      setError('NACHA generation failed: ' + (err.message || 'unknown error'));
    } finally {
      setSettlementLoading(false);
    }
  };

  // Export Summary — exactly what's on the portal view (the Surcharge Splits table):
  // Location, Terminal, Payee, Account (masked), Split/Txn, Txns, Amount + Total row.
  const handleExportSummary = () => {
    const splitRecords = (settlementData?.records || [])
      .filter(r => r.deposit_type === 'SRCH' && r.source_type === 'SPLITS');
    if (splitRecords.length === 0) {
      setError('No surcharge split data available to export');
      return;
    }

    const headers = ['Location', 'Terminal', 'Payee', 'Account', 'Split/Txn', 'Txns', 'Amount'];
    const csvRows = splitRecords.map(r => [
      r.location_name || '',
      r.terminal_number || '',
      r.name_on_account || '',
      maskAccount(r.account),
      parseFloat(r.split_amount).toFixed(2),
      r.transaction_count,
      parseFloat(r.amount).toFixed(2)
    ]);
    const totalAmt = splitRecords.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    csvRows.push(['', '', '', '', '', 'Total Surcharge Splits', totalAmt.toFixed(2)]);

    const csvContent = [
      headers.join(','),
      ...csvRows.map(row => row.map(cell => {
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `surcharge_summary_${format(settlementStartDate, 'yyyyMMdd')}_${format(settlementEndDate, 'yyyyMMdd')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setSuccess('Surcharge export generated successfully');
  };

  // Export SURCHARGE ACH — the per-bank-account payment file (one row per unique
  // routing+account, like gh-surcharge-payments-*.csv):
  //   NameOnAccount,Routing,Account,Transactions,SplitPerTxn,AmountOwed + TOTAL row.
  // Full numbers — this is the file payments are made from.
  const exportSurchargePaymentsFile = () => {
    const splitRecords = (settlementData?.records || [])
      .filter(r => r.deposit_type === 'SRCH' && r.source_type === 'SPLITS');
    if (splitRecords.length === 0) {
      setError('No surcharge split data available for ACH export');
      return;
    }

    const grouped = {};
    splitRecords.forEach(r => {
      const key = `${r.route || ''}|${r.account || ''}`;
      if (!grouped[key]) {
        grouped[key] = {
          name: r.name_on_account || '',
          route: r.route || '',
          account: r.account || '',
          transactions: 0,
          splits: new Set(),
          owed: 0
        };
      }
      grouped[key].transactions += parseInt(r.transaction_count) || 0;
      grouped[key].splits.add(parseFloat(r.split_amount).toFixed(2));
      grouped[key].owed += parseFloat(r.amount) || 0;
    });

    const rows = Object.values(grouped).sort((a, b) => b.owed - a.owed);
    const totalTxns = rows.reduce((s, r) => s + r.transactions, 0);
    const totalOwed = rows.reduce((s, r) => s + r.owed, 0);

    const headers = ['NameOnAccount', 'Routing', 'Account', 'Transactions', 'SplitPerTxn', 'AmountOwed'];
    const csvRows = rows.map(r => [
      r.name,
      r.route,
      r.account,
      r.transactions,
      r.splits.size === 1 ? [...r.splits][0] : 'various',
      r.owed.toFixed(2)
    ]);
    csvRows.push(['TOTAL', '', '', totalTxns, '', totalOwed.toFixed(2)]);

    const csvContent = [
      headers.join(','),
      ...csvRows.map(row => row.map(cell => {
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `surcharge-payments-${format(settlementStartDate, 'yyyy-MM-dd')}_to_${format(settlementEndDate, 'MM-dd')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setSuccess('Surcharge ACH export generated successfully');
  };

  // Export ACH — runs the export for whichever types are shown:
  //   SETL → the ORIGINAL settlement ACH file (unchanged from before splits work)
  //   SRCH → the per-bank-account surcharge payments file above
  const handleExportACH = () => {
    const wantSETL = settlementReportTypes.includes('SETL');
    const wantSRCH = settlementReportTypes.includes('SRCH') && settlementData?.srchBasis === 'SPLITS';
    if (!wantSETL && !wantSRCH) {
      setError('Select Settlement and/or Surcharge to export');
      return;
    }
    if (wantSETL) handleExportSettlementCSV();
    if (wantSRCH) exportSurchargePaymentsFile();
  };

  // Export settlement report to ACH CSV format — ORIGINAL behavior, untouched.
  // Format: Date,Location,Route,Account,Approved Transactions,Total Withdrawals
  const handleExportSettlementCSV = () => {
    if (!settlementData || !settlementData.records || settlementData.records.length === 0) {
      setError('No data available to export');
      return;
    }

    // Filter to only SETL (settlement/withdrawal) records for ACH export
    const settlementRecords = settlementData.records.filter(r => r.deposit_type === 'SETL');

    if (settlementRecords.length === 0) {
      setError('No settlement records available for ACH export');
      return;
    }

    // Aggregate to ONE LINE PER LOCATION (each location settles to its single vault
    // route+account) across the whole selected range. The old date+location grouping
    // was built for single-day runs — where it also yields one line per location —
    // but produced per-day rows once date ranges arrived (never the intent).
    const dateLabel = format(settlementStartDate, 'yyyy-MM-dd') === format(settlementEndDate, 'yyyy-MM-dd')
      ? format(settlementStartDate, 'yyyy-MM-dd')
      : `${format(settlementStartDate, 'yyyy-MM-dd')} to ${format(settlementEndDate, 'yyyy-MM-dd')}`;

    const aggregated = {};
    let totalTransactions = 0;
    let totalWithdrawals = 0;

    settlementRecords.forEach(record => {
      const location = record.location_name || '';
      const route = record.route || '';
      const account = record.account || '';

      const key = `${location}|${route}|${account}`;

      if (!aggregated[key]) {
        aggregated[key] = {
          date: dateLabel,
          location,
          route,
          account,
          transactions: 0,
          withdrawals: 0
        };
      }

      aggregated[key].transactions += parseInt(record.transaction_count) || 0;
      aggregated[key].withdrawals += parseFloat(record.amount) || 0;
      totalTransactions += parseInt(record.transaction_count) || 0;
      totalWithdrawals += parseFloat(record.amount) || 0;
    });

    // One row per location, sorted by location name
    const rows = Object.values(aggregated).sort((a, b) => a.location.localeCompare(b.location));

    // Build CSV in ACH format
    const headers = ['Date', 'Location', 'Route', 'Account', 'Approved Transactions', 'Total Withdrawals'];

    const csvRows = rows.map(row => [
      row.date,
      row.location,
      row.route,
      row.account,
      row.transactions,
      Math.round(row.withdrawals) // ACH typically uses whole numbers
    ]);

    // Add blank row and totals
    csvRows.push(['', '', '', '', '', '']);
    csvRows.push(['', 'TOTALS', '', '', totalTransactions, Math.round(totalWithdrawals)]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...csvRows.map(row => row.map(cell => {
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // Build filename in pai_daily_export format
    const dateStr = format(settlementEndDate, 'yyyyMMdd');
    link.download = `pai_daily_export_${dateStr}.csv`;

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setSuccess('ACH export generated successfully');
  };

  // Reconciliation Export handlers
  const handleReconciliationExport = async () => {
    try {
      setReconciliationWarning('');
      const payload = {
        reportType: 'reconciliation-export',
        location: reconciliationLocation,
        startDate: format(reconciliationStartDate, 'yyyy-MM-dd'),
        endDate: format(reconciliationEndDate, 'yyyy-MM-dd'),
        format: 'json'
      };
      
      const response = await apiCall('/api/v1/pai/download-report', 'POST', payload);
      
      if (response.warning) {
        setReconciliationWarning(response.warning);
      }
      
      setReconciliationData(response);
      setSuccess('Export generated successfully');
    } catch (error) {
      console.error('Reconciliation export error:', error);
      setError(error.message || 'Failed to export reconciliation data');
    }
  };
  
  const downloadReconciliationCSV = () => {
    if (!reconciliationData || !reconciliationData.transactions) return;

    // Convert transactions to CSV with Site Name and Settled Date
    const headers = ['Site Name', 'Settled Date', 'Date', 'Time', 'Terminal', 'Sequence', 'Amount', 'Surcharge', 'Status', 'Method'];
    const rows = reconciliationData.transactions.map(t => [
      reconciliationLocation,  // Site Name
      t.transaction_date,      // Using transaction_date as settled date
      t.transaction_date,
      t.transaction_time,
      t.terminal,
      t.sequence_number,
      t.amount,
      t.surcharge || '0.00',
      t.status,
      t.method
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => {
        // Escape quotes and wrap in quotes if needed
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(','))
    ].join('\n');

    // Download CSV
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reconciliation_${reconciliationLocation}_${format(reconciliationStartDate, 'yyyy-MM-dd')}_${format(reconciliationEndDate, 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (!hasPermission('manage_pai_accounts')) {
    return null;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Paper elevation={1} sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <DashboardIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Settlement Dashboard
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} variant="scrollable" scrollButtons="auto">
            {/* Tabs carry named values (not positions) so they can be reordered/hidden without renumbering the bodies.
                Hidden 2026-09-12 (Todd): Reconciliation, PAI Historical, Interchange — their bodies are kept below, unreachable. */}
            <Tab value="overview" label="Overview" icon={<DashboardIcon />} iconPosition="start" />
            <Tab value="mappings" label="Account Mappings" icon={<AccountIcon />} iconPosition="start" />
            <Tab value="settlement-reports" label="Settlement Reports" icon={<AssessmentIcon />} iconPosition="start" />
            {userProfile?.role === 'admin' && <Tab value="bank-reconciliation" label="Bank Reconciliation" icon={<BankIcon />} iconPosition="start" />}
            <Tab value="import" label="Manual Import" icon={<FileIcon />} iconPosition="start" />
            <Tab value="batches" label="Settlement Batches" icon={<HistoryIcon />} iconPosition="start" />
            <Tab value="settings" label="Settings" icon={<SettingsIcon />} iconPosition="start" />
            <Tab value="ach" label="ACH Management" icon={<HistoryIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Dashboard Tab */}
        {tabValue === 'overview' && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ mt: 3 }}>
              {/* Header */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box display="flex" gap={2} alignItems="center">
                  {/* View Mode Toggle */}
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewModeChange}
                    size="small"
                  >
                    <ToggleButton value="single">
                      <CalendarIcon sx={{ mr: 1 }} />
                      Single Day
                    </ToggleButton>
                    <ToggleButton value="range">
                      <DateRangeIcon sx={{ mr: 1 }} />
                      Date Range
                    </ToggleButton>
                  </ToggleButtonGroup>
                  
                  {/* Date Selection */}
                  {viewMode === 'single' ? (
                    <DatePicker
                      label="Select Date"
                      value={selectedDate}
                      onChange={(newDate) => setSelectedDate(newDate)}
                      renderInput={(params) => <TextField {...params} size="small" />}
                    />
                  ) : (
                    <>
                      <DatePicker
                        label="Start Date"
                        value={startDate}
                        onChange={(newDate) => setStartDate(newDate)}
                        renderInput={(params) => <TextField {...params} size="small" />}
                      />
                      <DatePicker
                        label="End Date"
                        value={endDate}
                        onChange={(newDate) => setEndDate(newDate)}
                        renderInput={(params) => <TextField {...params} size="small" />}
                      />
                    </>
                  )}

                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                    disabled={exportLoading}
                  >
                    {exportLoading ? 'Exporting...' : 'Export'}
                  </Button>
                  <Menu
                    anchorEl={exportMenuAnchor}
                    open={Boolean(exportMenuAnchor)}
                    onClose={() => setExportMenuAnchor(null)}
                  >
                    <MenuItem onClick={handleExportCSV}>
                      <ListItemIcon>
                        <FileIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Export as CSV</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleExportNACHA}>
                      <ListItemIcon>
                        <BankIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Export as NACHA</ListItemText>
                    </MenuItem>
                  </Menu>
                </Box>
              </Box>

              {/* Location Selection Section */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <LocationIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'middle' }} />
                    Select Locations
                  </Typography>

                  {/* Selected Locations Chips */}
                  {selectedLocations.length > 0 && (
                    <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selectedLocations.map(location => (
                        <Chip
                          key={location}
                          label={location}
                          size="small"
                          onDelete={() => {
                            setSelectedLocations(prev => prev.filter(l => l !== location));
                          }}
                          color="primary"
                          sx={{ maxWidth: '200px' }}
                        />
                      ))}
                      {selectedLocations.length > 1 && (
                        <Chip
                          label="Clear All"
                          size="small"
                          onClick={() => setSelectedLocations([])}
                          variant="outlined"
                          color="error"
                          sx={{ cursor: 'pointer' }}
                        />
                      )}
                    </Box>
                  )}

                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search locations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {availableLocations.filter(loc =>
                        loc.toLowerCase().includes(searchTerm.toLowerCase())
                      ).length} of {availableLocations.length} locations
                    </Typography>
                    {selectedLocations.length > 0 && (
                      <Typography variant="body2" color="primary">
                        {selectedLocations.length} selected
                      </Typography>
                    )}
                  </Box>

                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedLocations.length === availableLocations.length && availableLocations.length > 0}
                          indeterminate={selectedLocations.length > 0 && selectedLocations.length < availableLocations.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedLocations(availableLocations);
                            } else {
                              setSelectedLocations([]);
                            }
                          }}
                        />
                      }
                      label={<strong>Select All</strong>}
                      sx={{ mb: 1, borderBottom: '1px solid #e0e0e0', pb: 1 }}
                    />

                    <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {availableLocations
                        .filter(location => location.toLowerCase().includes(searchTerm.toLowerCase()))
                        .map(location => (
                          <FormControlLabel
                            key={location}
                            control={
                              <Checkbox
                                checked={selectedLocations.includes(location)}
                                onChange={() => {
                                  if (selectedLocations.includes(location)) {
                                    setSelectedLocations(prev => prev.filter(l => l !== location));
                                  } else {
                                    setSelectedLocations(prev => [...prev, location]);
                                  }
                                }}
                                size="small"
                              />
                            }
                            label={location}
                            sx={{
                              display: 'flex',
                              width: '100%',
                              ml: 0,
                              mr: 0,
                              backgroundColor: selectedLocations.includes(location) ? 'action.hover' : 'transparent',
                              borderRadius: 1,
                              mb: 0,
                              '& .MuiFormControlLabel-label': {
                                width: '100%',
                                fontWeight: selectedLocations.includes(location) ? 'medium' : 'normal'
                              }
                            }}
                          />
                        ))}
                    </Box>
                  </FormGroup>
                </CardContent>
              </Card>

              {/* Transaction Type Selection */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <AssessmentIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'middle' }} />
                    Select Transaction Types
                  </Typography>

                  {/* Selected Types Chips */}
                  {selectedTypes.length > 0 && (
                    <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selectedTypes.map(type => (
                        <Chip
                          key={type}
                          label={type}
                          size="small"
                          onDelete={() => {
                            if (selectedTypes.length > 1) { // Keep at least one selected
                              setSelectedTypes(prev => prev.filter(t => t !== type));
                            }
                          }}
                          color="primary"
                          sx={{ maxWidth: '200px' }}
                        />
                      ))}
                    </Box>
                  )}

                  <FormGroup row>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedTypes.includes('Settlement')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTypes(prev => [...prev, 'Settlement']);
                            } else if (selectedTypes.length > 1) {
                              setSelectedTypes(prev => prev.filter(t => t !== 'Settlement'));
                            }
                          }}
                          size="small"
                        />
                      }
                      label="Settlement (Withdrawals)"
                      sx={{ mr: 3 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedTypes.includes('Surcharge')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTypes(prev => [...prev, 'Surcharge']);
                            } else if (selectedTypes.length > 1) {
                              setSelectedTypes(prev => prev.filter(t => t !== 'Surcharge'));
                            }
                          }}
                          size="small"
                        />
                      }
                      label="Surcharge Fees"
                    />
                  </FormGroup>

                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Select which transaction types to include in the export
                  </Typography>
                </CardContent>
              </Card>

              {dashboardLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {/* Summary Statistics */}
                  <Grid container spacing={3} mb={3}>
                    <Grid item xs={12} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>
                            Approved Transactions
                          </Typography>
                          <Typography variant="h4">
                            {formatNumber(dashboardData.dailyStats.totalApproved)}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            of {formatNumber(dashboardData.dailyStats.totalTransactions)} total
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>
                            Total Withdrawals
                          </Typography>
                          <Typography variant="h4">
                            {formatCurrency(dashboardData.dailyStats.totalWithdrawalAmount)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>
                            Total Surcharge
                          </Typography>
                          <Typography variant="h4">
                            {formatCurrency(dashboardData.dailyStats.totalSurcharge)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>
                            Active Terminals
                          </Typography>
                          <Typography variant="h4">
                            {formatNumber(dashboardData.dailyStats.uniqueTerminals)}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            of {formatNumber(dashboardData.summaryStats.totalTerminals)} total
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                  
                  {/* Response Breakdown and Top Terminals */}
                  <Grid container spacing={3}>
                    {/* Response Breakdown */}
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Transaction Response Breakdown
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Response</TableCell>
                                  <TableCell align="right">Count</TableCell>
                                  <TableCell align="right">Amount</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {dashboardData.responseBreakdown.map((response, index) => (
                                  <TableRow key={index}>
                                    <TableCell>
                                      <Box display="flex" alignItems="center">
                                        {response.response === 'Approved' ? (
                                          <CheckCircleIcon color="success" sx={{ mr: 1, fontSize: 20 }} />
                                        ) : (
                                          <CancelIcon color="error" sx={{ mr: 1, fontSize: 20 }} />
                                        )}
                                        {response.response}
                                      </Box>
                                    </TableCell>
                                    <TableCell align="right">{formatNumber(response.count)}</TableCell>
                                    <TableCell align="right">{formatCurrency(response.amount)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    {/* Top Terminals */}
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Top 10 Terminals by Volume
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Terminal</TableCell>
                                  <TableCell>Location/Business</TableCell>
                                  <TableCell align="right">Withdrawals</TableCell>
                                  <TableCell align="right">Surcharge</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {dashboardData.topTerminals.map((terminal, index) => (
                                  <TableRow key={index}>
                                    <TableCell>{terminal.terminal}</TableCell>
                                    <TableCell>{terminal.location || terminal.businessName || '-'}</TableCell>
                                    <TableCell align="right">{formatCurrency(terminal.withdrawalAmount)}</TableCell>
                                    <TableCell align="right">{formatCurrency(terminal.surchargeAmount)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                  
                  {/* Quick Date Navigation */}
                  <Card sx={{ mt: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Quick Date Navigation
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {dashboardData.availableDates.map((date) => (
                          <Button
                            key={date}
                            size="small"
                            variant={
                              viewMode === 'single' && format(selectedDate, 'yyyy-MM-dd') === date
                                ? 'contained'
                                : 'outlined'
                            }
                            onClick={() => {
                              setViewMode('single');
                              setSelectedDate(createLocalDate(date));
                            }}
                          >
                            {format(createLocalDate(date), 'MM/dd')}
                          </Button>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </>
              )}
              
              {/* Monthly Surcharge Export Card */}
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Monthly Surcharge Export
                  </Typography>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Export monthly surcharge data with account mappings
                  </Typography>
                  
                  <Box display="flex" gap={2} alignItems="center" mt={2}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Month</InputLabel>
                      <Select
                        value={exportMonth}
                        label="Month"
                        onChange={(e) => setExportMonth(e.target.value)}
                      >
                        <MenuItem value={1}>January</MenuItem>
                        <MenuItem value={2}>February</MenuItem>
                        <MenuItem value={3}>March</MenuItem>
                        <MenuItem value={4}>April</MenuItem>
                        <MenuItem value={5}>May</MenuItem>
                        <MenuItem value={6}>June</MenuItem>
                        <MenuItem value={7}>July</MenuItem>
                        <MenuItem value={8}>August</MenuItem>
                        <MenuItem value={9}>September</MenuItem>
                        <MenuItem value={10}>October</MenuItem>
                        <MenuItem value={11}>November</MenuItem>
                        <MenuItem value={12}>December</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <TextField
                      size="small"
                      label="Year"
                      type="number"
                      value={exportYear}
                      onChange={(e) => setExportYear(parseInt(e.target.value) || new Date().getFullYear())}
                      inputProps={{ min: 2020, max: new Date().getFullYear() + 1 }}
                      sx={{ width: 100 }}
                    />
                    
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<DownloadIcon />}
                      onClick={handleExportMonthlySurcharge}
                      disabled={exportingMonthlySurcharge}
                    >
                      {exportingMonthlySurcharge ? 'Exporting...' : 'Export'}
                    </Button>
                  </Box>
                  
                  <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                    This export includes all approved transactions with surcharges for the selected month,
                    along with vault and surcharge account mappings.
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </LocalizationProvider>
        )}

        {/* Account Mappings Tab */}
        {tabValue === 'mappings' && (
          <Box sx={{ mt: 3 }}>
            <PaiAccountMappings />
          </Box>
        )}

        {/* Settlement Reports Tab — admin-only, processor-based, reconciled 12am-12am */}
        {tabValue === 'settlement-reports' && (
          <Box sx={{ mt: 3 }}>
            <Paper sx={{ p: 3 }}>
              {userProfile?.role !== 'admin' ? (
                <Alert severity="warning">
                  Settlement Reports are restricted to administrators.
                </Alert>
              ) : (
              <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Settlement Reports
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Reconciled settlement by processor — calendar day (12am–12am), single source of truth.
                  Shows every location that settled in the date range.
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="settlement-processor-label">Processor</InputLabel>
                    <Select
                      labelId="settlement-processor-label"
                      label="Processor"
                      value={settlementProcessor}
                      onChange={(e) => setSettlementProcessor(e.target.value)}
                    >
                      <MenuItem value="SWITCH">Switch Commerce</MenuItem>
                      <MenuItem value="DNS" disabled>DNS (coming soon)</MenuItem>
                      <MenuItem value="PAI" disabled>PAI (coming soon)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Start Date"
                      value={settlementStartDate}
                      onChange={(newValue) => setSettlementStartDate(newValue)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="End Date"
                      value={settlementEndDate}
                      onChange={(newValue) => setSettlementEndDate(newValue)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={fetchSettlementByProcessor}
                    disabled={!settlementProcessor || settlementLoading}
                    sx={{ height: '56px' }}
                    startIcon={settlementLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                  >
                    {settlementLoading ? 'Loading...' : 'View Settlement'}
                  </Button>
                </Grid>
              </Grid>

              {/* Type Filter */}
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">Show:</Typography>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={settlementReportTypes.includes('SETL')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSettlementReportTypes(prev => [...prev, 'SETL']);
                          } else if (settlementReportTypes.length > 1) {
                            setSettlementReportTypes(prev => prev.filter(t => t !== 'SETL'));
                          }
                        }}
                        size="small"
                        color="success"
                      />
                    }
                    label={<Typography variant="body2">Settlement (SETL)</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={settlementReportTypes.includes('SRCH')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSettlementReportTypes(prev => [...prev, 'SRCH']);
                          } else if (settlementReportTypes.length > 1) {
                            setSettlementReportTypes(prev => prev.filter(t => t !== 'SRCH'));
                          }
                        }}
                        size="small"
                        color="info"
                      />
                    }
                    label={<Typography variant="body2">Surcharge (SRCH)</Typography>}
                  />
                </FormGroup>
              </Box>

              {settlementData && (
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1">
                      Results — {settlementData.locationCount || 0} location{(settlementData.locationCount === 1) ? '' : 's'} settled
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        label="Effective Date"
                        type="date"
                        size="small"
                        value={achEffectiveDate}
                        onChange={(e) => setAchEffectiveDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 165 }}
                        helperText={achEffectiveDate ? 'applies to Generate/Send/Resend' : 'blank = next banking day'}
                      />
                      <Button
                        variant="outlined"
                        color="success"
                        startIcon={<DownloadIcon />}
                        onClick={handleGenerateNacha}
                        size="small"
                        disabled={settlementLoading || achSending}
                      >
                        Generate NACHA (SET)
                      </Button>
                      <Button
                        variant="contained"
                        color="warning"
                        startIcon={<HistoryIcon />}
                        onClick={handleSendACH}
                        size="small"
                        disabled={settlementLoading || achSending}
                      >
                        {achSending ? 'Sending…' : 'Send ACH'}
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<HistoryIcon />}
                        onClick={() => { setResendTxId(''); setResendConfirmText(''); setResendDialogOpen(true); }}
                        size="small"
                        disabled={settlementLoading || achSending || !(settlementData.achStatus || []).some(s => s.status === 'SENT')}
                      >
                        Resend ACH
                      </Button>
                      {settlementData.srchBasis === 'SPLITS' && settlementReportTypes.includes('SRCH') && (
                        <>
                          <Button
                            variant="outlined"
                            color="info"
                            startIcon={<DownloadIcon />}
                            onClick={handleGenerateNachaSrch}
                            size="small"
                            disabled={settlementLoading || achSending}
                          >
                            Generate NACHA (SRCH)
                          </Button>
                          <Button
                            variant="contained"
                            color="warning"
                            startIcon={<HistoryIcon />}
                            onClick={handleSendSurchargeACH}
                            size="small"
                            disabled={settlementLoading || achSending}
                          >
                            {achSending ? 'Sending…' : 'Send Surcharge ACH'}
                          </Button>
                          <Button
                            variant="contained"
                            color="info"
                            startIcon={<DownloadIcon />}
                            onClick={handleExportSummary}
                            size="small"
                          >
                            Export Summary
                          </Button>
                        </>
                      )}
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportACH}
                        size="small"
                      >
                        Export ACH
                      </Button>
                    </Box>
                  </Box>
                  {(((settlementData.achStatus || []).length > 0 && settlementReportTypes.includes('SETL'))
                    || (settlementData.achSrchStatus && settlementReportTypes.includes('SRCH'))) && (
                    <Box sx={{ mb: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        ACH Send Status{settlementReportTypes.includes('SETL') ? ' (vault / SET files)' : ' (surcharge / SRCH file)'}
                      </Typography>
                      {settlementReportTypes.includes('SETL') && (settlementData.achStatus || []).map((s) => {
                        const delta = s.fileTotal !== undefined
                          ? (parseFloat(s.settledTotal) - parseFloat(s.fileTotal)).toFixed(2)
                          : null;
                        return (
                          <Box key={s.businessDate} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="body2" sx={{ minWidth: 90, fontFamily: 'monospace' }}>{s.businessDate}</Typography>
                            {s.status === 'NOT_SENT' ? (
                              <Chip size="small" color="error" label={`NOT SENT — $${s.settledTotal} settled`} />
                            ) : s.matches ? (
                              <Chip
                                size="small"
                                color="success"
                                label={`${s.fileName} — $${s.fileTotal}, sent ${s.sentAt ? format(parseISO(s.sentAt), 'M/d h:mm a') : '?'}${s.sentBy ? ` by ${s.sentBy}` : ''}`}
                              />
                            ) : (
                              <Chip
                                size="small"
                                color="warning"
                                label={`${s.fileName} — file $${s.fileTotal} vs settled $${s.settledTotal} (Δ $${delta}) — batches changed after send`}
                              />
                            )}
                          </Box>
                        );
                      })}
                      {settlementData.achSrchStatus && settlementReportTypes.includes('SRCH') && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: settlementReportTypes.includes('SETL') ? 1 : 0, pt: settlementReportTypes.includes('SETL') ? 1 : 0, borderTop: settlementReportTypes.includes('SETL') ? 1 : 0, borderColor: 'divider' }}>
                          <Typography variant="body2" sx={{ minWidth: 90, fontWeight: 'bold' }}>Surcharge</Typography>
                          {settlementData.achSrchStatus.status === 'NOT_SENT' && !settlementData.achSrchStatus.due ? (
                            <Chip size="small" variant="outlined" label={`$${settlementData.achSrchStatus.currentTotal} accrued — sent monthly, due after month end`} />
                          ) : settlementData.achSrchStatus.status === 'NOT_SENT' ? (
                            <Chip size="small" color="error" label={`NOT SENT — $${settlementData.achSrchStatus.currentTotal} owed for this range`} />
                          ) : settlementData.achSrchStatus.matches ? (
                            <Chip
                              size="small"
                              color="success"
                              label={`${settlementData.achSrchStatus.fileName} — $${settlementData.achSrchStatus.fileTotal}, sent ${settlementData.achSrchStatus.sentAt ? format(parseISO(settlementData.achSrchStatus.sentAt), 'M/d h:mm a') : '?'}`}
                            />
                          ) : (
                            <Chip
                              size="small"
                              color="warning"
                              label={`${settlementData.achSrchStatus.fileName} — file $${settlementData.achSrchStatus.fileTotal} vs current $${settlementData.achSrchStatus.currentTotal} — splits changed after send`}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                  <Dialog open={resendDialogOpen} onClose={() => setResendDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Resend ACH File</DialogTitle>
                    <DialogContent>
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        This re-pushes a file the bank already received. Only do this when the bank
                        confirms they did NOT process the original. The exact same file (same trace
                        numbers) is sent again, and this resend is recorded under your user.
                      </Alert>
                      {achEffectiveDate && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Effective Date override is set to <strong>{achEffectiveDate}</strong> — the archived
                          file&apos;s effective date will be re-stamped to this date before re-sending
                          (entries and trace numbers stay identical).
                        </Alert>
                      )}
                      <TextField
                        select fullWidth label="File to resend" sx={{ mb: 2 }}
                        value={resendTxId}
                        onChange={(e) => setResendTxId(e.target.value)}
                      >
                        {(settlementData.achStatus || []).filter(s => s.status === 'SENT').map(s => (
                          <MenuItem key={s.transmissionId} value={s.transmissionId}>
                            {s.fileName} — {s.businessDate} — ${s.fileTotal} (sent {s.sentAt ? format(parseISO(s.sentAt), 'M/d h:mm a') : '?'}{s.sentBy ? ` by ${s.sentBy}` : ''})
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        fullWidth
                        label='Type CONFIRM to enable resend'
                        value={resendConfirmText}
                        onChange={(e) => setResendConfirmText(e.target.value)}
                        autoComplete="off"
                      />
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setResendDialogOpen(false)}>Cancel</Button>
                      <Button
                        variant="contained" color="error"
                        disabled={resendConfirmText !== 'CONFIRM' || !resendTxId || achSending}
                        onClick={handleResendACH}
                      >
                        Resend File
                      </Button>
                    </DialogActions>
                  </Dialog>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {settlementReportTypes.includes('SETL') && (
                      <Grid item xs={3}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="body2" color="text.secondary">Total Settlement</Typography>
                            <Typography variant="h6" color="success.main">${(settlementData.totalSettlement || 0).toFixed(2)}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                    {settlementReportTypes.includes('SRCH') && (
                      <Grid item xs={3}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="body2" color="text.secondary">Total Surcharge</Typography>
                            <Typography variant="h6" color="info.main">${(settlementData.totalSurcharge || 0).toFixed(2)}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                    <Grid item xs={3}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="text.secondary">Filtered Records</Typography>
                          <Typography variant="h6">
                            {settlementData.records ? settlementData.records.filter(r => settlementReportTypes.includes(r.deposit_type)).length : 0}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={3}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="text.secondary">Source</Typography>
                          <Typography variant="h6">{settlementData.source || 'Multiple'}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {settlementData.records && settlementData.records.length > 0 && (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Location</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell>Source</TableCell>
                            <TableCell>ACH File</TableCell>
                            <TableCell>Sent By</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {settlementData.records
                            .filter(record => settlementReportTypes.includes(record.deposit_type)
                              && !(record.deposit_type === 'SRCH' && record.source_type === 'SPLITS'))
                            .map((record, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{record.settlement_date ? record.settlement_date.substring(0, 10).replace(/(\d{4})-(\d{2})-(\d{2})/, '$2/$3/$1') : ''}</TableCell>
                              <TableCell>{record.location_name}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={record.deposit_type === 'SETL' ? 'Settlement' : 'Surcharge'}
                                  color={record.deposit_type === 'SETL' ? 'success' : 'info'}
                                />
                              </TableCell>
                              <TableCell align="right">${parseFloat(record.amount).toFixed(2)}</TableCell>
                              <TableCell>{record.source_type || 'RECONCILED'}</TableCell>
                              <TableCell>
                                {record.deposit_type !== 'SETL' ? (
                                  <Typography variant="body2" color="text.secondary">—</Typography>
                                ) : record.ach_file ? (
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{record.ach_file}</Typography>
                                ) : (
                                  <Chip size="small" color="error" label="NOT SENT" />
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                  {record.deposit_type === 'SETL' ? (record.ach_sent_by || '—') : '—'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {/* Surcharge Splits — the per-payee amounts TFI pays, computed from
                      settlement_transactions × PAI surcharge accounts (source SPLITS). */}
                  {settlementData.srchBasis === 'SPLITS' && settlementReportTypes.includes('SRCH') && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Surcharge Splits — payees for {format(settlementStartDate, 'MM/dd/yyyy')} to {format(settlementEndDate, 'MM/dd/yyyy')}
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Location</TableCell>
                              <TableCell>Terminal</TableCell>
                              <TableCell>Payee</TableCell>
                              <TableCell>Account</TableCell>
                              <TableCell align="right">Split/Txn</TableCell>
                              <TableCell align="right">Txns</TableCell>
                              <TableCell align="right">Amount</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {settlementData.records
                              .filter(r => r.deposit_type === 'SRCH' && r.source_type === 'SPLITS')
                              .map((r, idx) => (
                              <TableRow key={idx} sx={r.exceeds_collected ? { backgroundColor: '#fff8e1' } : undefined}>
                                <TableCell>{r.location_name}</TableCell>
                                <TableCell>{r.terminal_number}</TableCell>
                                <TableCell>
                                  {r.name_on_account}
                                  {r.exceeds_collected && (
                                    <Chip size="small" color="warning" variant="outlined" label="splits exceed collected" sx={{ ml: 1 }} />
                                  )}
                                </TableCell>
                                <TableCell>{maskAccount(r.account)}</TableCell>
                                <TableCell align="right">${parseFloat(r.split_amount).toFixed(2)}</TableCell>
                                <TableCell align="right">{r.transaction_count}</TableCell>
                                <TableCell align="right">${parseFloat(r.amount).toFixed(2)}</TableCell>
                              </TableRow>
                            ))}
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                              <TableCell colSpan={6} sx={{ fontWeight: 'bold' }}>Total Surcharge Splits</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                ${(settlementData.totalSurcharge || 0).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </Box>
              )}
              </>
              )}
            </Paper>
          </Box>
        )}

        {/* Bank Reconciliation (Platform B) — Switch CASH deposits vs settled cash; admin-only */}
        {tabValue === 'bank-reconciliation' && userProfile?.role === 'admin' && (
          <BankReconciliationTab />
        )}

        {/* ACH Management Tab — transmission ledger: sent acknowledgements, failures, S3 archive links */}
        {tabValue === 'ach' && (
          <Box sx={{ mt: 3 }}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  ACH Management
                </Typography>
                <Button variant="outlined" size="small" onClick={loadAchHistory} disabled={achLoading}>
                  {achLoading ? 'Loading…' : 'Refresh'}
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Every ACH file generated for sending is archived to S3 and recorded here with its
                bank transmission result. SENT = delivered to the bank SFTP and size-verified.
              </Typography>
              {achTransmissions.length === 0 && !achLoading ? (
                <Typography color="text.secondary">No ACH transmissions yet. Use "Send ACH" on the Settlement Reports tab.</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>File</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Business Date</TableCell>
                        <TableCell>Effective</TableCell>
                        <TableCell align="right">Entries</TableCell>
                        <TableCell align="right">Total Credits</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Sent At / By</TableCell>
                        <TableCell>File</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {achTransmissions.map(t => (
                        <TableRow key={t.id} hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{t.file_name}</TableCell>
                          <TableCell><Chip size="small" label={t.file_type} color={t.file_type === 'SET' ? 'success' : 'info'} variant="outlined" /></TableCell>
                          <TableCell>{t.business_date ? t.business_date.substring(0, 10) : ''}</TableCell>
                          <TableCell>{t.effective_date ? t.effective_date.substring(0, 10) : ''}</TableCell>
                          <TableCell align="right">{t.entry_count}</TableCell>
                          <TableCell align="right">${parseFloat(t.total_credits || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={t.status}
                              color={t.status === 'SENT' ? 'success' : t.status === 'FAILED' ? 'error' : 'default'}
                            />
                            {t.status === 'FAILED' && t.error_message && (
                              <Typography variant="caption" display="block" color="error" sx={{ maxWidth: 260 }}>
                                {t.error_message}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {t.sent_at ? new Date(t.sent_at).toLocaleString() : '—'}
                            {t.sent_by && <Typography variant="caption" display="block" color="text.secondary">{t.sent_by}</Typography>}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Button size="small" startIcon={<DownloadIcon />} onClick={() => handleAchDownload(t.id)}>
                                S3
                              </Button>
                              {t.status === 'FAILED' && (
                                <Button
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  onClick={() => handleAchResend(t.id, t.file_name)}
                                  disabled={achSending}
                                >
                                  Resend
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Box>
        )}

        {/* Manual Import Tab (NEW) */}
        {tabValue === 'import' && (
          <Box sx={{ mt: 3 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                <UploadIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Manual Settlement Import
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Import settlement data from DNS, Switch Commerce, or Processor TransactionDetail files
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Processor</InputLabel>
                    <Select
                      value={importProcessor}
                      onChange={(e) => setImportProcessor(e.target.value)}
                      label="Processor"
                    >
                      <MenuItem value="DNS">DNS</MenuItem>
                      <MenuItem value="SwitchCommerce">Switch Commerce</MenuItem>
                      <MenuItem value="PROCESSOR">Processor TransactionDetail</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Button
                    component="label"
                    variant="outlined"
                    fullWidth
                    startIcon={importParsing ? <CircularProgress size={20} /> : <UploadIcon />}
                    sx={{ height: '56px' }}
                    disabled={importParsing}
                  >
                    {importFile ? importFile.name : 'Select File (CSV or XLSX)'}
                    <input
                      type="file"
                      hidden
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileSelect}
                    />
                  </Button>
                </Grid>
              </Grid>

              {importPreview && (
                <Box sx={{ mt: 3 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">File Preview</Typography>
                    <Typography variant="body2">
                      {importPreview.totalRecords} records found across {importPreview.dates.length} date(s)
                    </Typography>
                  </Alert>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" gutterBottom>Dates</Typography>
                          {importPreview.dates.map(date => (
                            <Box key={date} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2">{date}</Typography>
                              <Typography variant="body2">
                                SETL: ${importPreview.dateGroups[date].settlements.toFixed(2)} |
                                SRCH: ${importPreview.dateGroups[date].surcharges.toFixed(2)}
                              </Typography>
                            </Box>
                          ))}
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" gutterBottom>Terminals</Typography>
                          {importPreview.terminals.map(t => (
                            <Chip key={t} label={t} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                          ))}
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" gutterBottom>Locations</Typography>
                          {importPreview.locations.map(l => (
                            <Chip key={l} label={l} size="small" sx={{ mr: 0.5, mb: 0.5 }} variant="outlined" />
                          ))}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleImportSubmit}
                      disabled={importSubmitting}
                      startIcon={importSubmitting ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                    >
                      {importSubmitting ? 'Importing...' : 'Import Records'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setImportFile(null);
                        setImportPreview(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              )}

              {importHistory.length > 0 && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Recent Imports
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>File</TableCell>
                          <TableCell>Processor</TableCell>
                          <TableCell align="right">Records</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {importHistory.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{format(new Date(item.date), 'MM/dd/yyyy HH:mm')}</TableCell>
                            <TableCell>{item.filename}</TableCell>
                            <TableCell>{item.processor}</TableCell>
                            <TableCell align="right">{item.records}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Paper>
          </Box>
        )}

        {/* Reconciliation Tab - moved to tab 4 */}
        {tabValue === 'reconciliation' && (
          <Box sx={{ mt: 3 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                <CompareArrowsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Transaction Reconciliation Export
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Export transaction data for reconciliation with customer records
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    placeholder="Search locations..."
                    value={locationSearchTerm}
                    onChange={(e) => setLocationSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    size="small"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Location</InputLabel>
                    <Select
                      value={reconciliationLocation}
                      onChange={(e) => setReconciliationLocation(e.target.value)}
                      label="Location"
                    >
                      <MenuItem value="">
                        <em>Select a location</em>
                      </MenuItem>
                      {(allLocations.length > 0 ? allLocations : paiLocations.map(l => ({ location_name: l })))
                        .filter(loc =>
                          (loc.location_name || loc).toLowerCase().includes(locationSearchTerm.toLowerCase())
                        )
                        .map((loc, idx) => (
                          <MenuItem key={idx} value={loc.location_name || loc}>
                            {loc.location_name || loc}
                            {loc.processor && (
                              <Chip size="small" label={loc.processor} sx={{ ml: 1 }} />
                            )}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Start Date"
                      value={reconciliationStartDate}
                      onChange={(newValue) => setReconciliationStartDate(newValue)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="End Date"
                      value={reconciliationEndDate}
                      onChange={(newValue) => setReconciliationEndDate(newValue)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={handleReconciliationExport}
                    disabled={!reconciliationLocation || !reconciliationStartDate || !reconciliationEndDate}
                    sx={{ height: '56px' }}
                  >
                    Export
                  </Button>
                </Grid>
              </Grid>

              {reconciliationWarning && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {reconciliationWarning}
                </Alert>
              )}

              {reconciliationData && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Export Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">Terminals</Typography>
                      <Typography variant="h6">{reconciliationData.gh_terminal_count || 0}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">Transactions</Typography>
                      <Typography variant="h6">{reconciliationData.gh_transaction_count || 0}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                      <Typography variant="h6">${(reconciliationData.gh_total_amount || 0).toFixed(2)}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={downloadReconciliationCSV}
                        startIcon={<GetAppIcon />}
                      >
                        Download CSV
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Paper>
          </Box>
        )}

        {/* PAI Historical Tab - Terminal Reports moved to tab 5 */}
        {tabValue === 'pai-historical' && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={3}>
                {/* Date Selection */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={2}>
                        <DatePicker
                          label="Report Date"
                          value={terminalReportDate}
                          onChange={(newDate) => setTerminalReportDate(newDate)}
                          renderInput={(params) => <TextField {...params} />}
                          maxDate={new Date()}
                        />
                        <Button
                          variant="contained"
                          startIcon={terminalReportLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
                          onClick={fetchTerminalReports}
                          disabled={terminalReportLoading || trxLogLoading}
                        >
                          {terminalReportLoading ? 'Fetching...' : 'Fetch Reports'}
                        </Button>
                        <Button
                          variant="contained"
                          color="secondary"
                          startIcon={trxLogLoading ? <CircularProgress size={20} /> : <Description />}
                          onClick={fetchTrxLogDetails}
                          disabled={trxLogLoading || terminalReportLoading || importLoading}
                        >
                          {trxLogLoading ? 'Loading...' : 'Fetch TrxLog Details'}
                        </Button>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={importLoading ? <CircularProgress size={20} /> : <SaveAlt />}
                          onClick={importTrxLogToDatabase}
                          disabled={trxLogLoading || terminalReportLoading || importLoading}
                        >
                          {importLoading ? 'Importing...' : 'Import TrxLog'}
                        </Button>
                        <Button
                          variant="contained"
                          sx={{ 
                            backgroundColor: '#FFC107', 
                            color: '#000',
                            '&:hover': { 
                              backgroundColor: '#FFB300' 
                            } 
                          }}
                          startIcon={reconcileLoading ? <CircularProgress size={20} /> : <CompareArrowsIcon />}
                          onClick={reconcileTransactions}
                          disabled={reconcileLoading || trxLogLoading || terminalReportLoading || importLoading}
                        >
                          {reconcileLoading ? 'Reconciling...' : 'Reconcile'}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Terminal Status Report */}
                {terminalStatusData && (
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Terminal Status Report
                        </Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Terminal</TableCell>
                                <TableCell>Location</TableCell>
                                <TableCell>Group</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Balance</TableCell>
                                <TableCell align="right">Transactions</TableCell>
                                <TableCell align="right">Total Amount</TableCell>
                                <TableCell align="right">Surcharge</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {terminalStatusData.terminals && terminalStatusData.terminals.map((terminal) => (
                                <TableRow key={terminal.terminal}>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight="bold">
                                      {terminal.terminal}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{terminal.location || '-'}</TableCell>
                                  <TableCell>{terminal.group || '-'}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={terminal.status}
                                      size="small"
                                      color={terminal.status === 'OK' ? 'success' : 'error'}
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    ${parseFloat(terminal.balance || 0).toFixed(2)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {terminal.transactionCount || 0}
                                  </TableCell>
                                  <TableCell align="right">
                                    ${parseFloat(terminal.totalAmount || 0).toFixed(2)}
                                  </TableCell>
                                  <TableCell align="right">
                                    ${parseFloat(terminal.totalSurcharge || 0).toFixed(2)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        
                        {/* Summary Stats */}
                        {terminalStatusData.summary && (
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={3}>
                                <Typography variant="body2" color="textSecondary">
                                  Total Terminals
                                </Typography>
                                <Typography variant="h6">
                                  {terminalStatusData.summary.terminalCount}
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography variant="body2" color="textSecondary">
                                  Total Transactions
                                </Typography>
                                <Typography variant="h6">
                                  {terminalStatusData.summary.totalTransactions}
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography variant="body2" color="textSecondary">
                                  Total Amount
                                </Typography>
                                <Typography variant="h6">
                                  ${parseFloat(terminalStatusData.summary.totalAmount || 0).toFixed(2)}
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography variant="body2" color="textSecondary">
                                  Total Surcharge
                                </Typography>
                                <Typography variant="h6">
                                  ${parseFloat(terminalStatusData.summary.totalSurcharge || 0).toFixed(2)}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Transaction Details */}
                {terminalTransactionData && terminalTransactionData.byLocation && (
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Transaction Details by Location
                        </Typography>
                        {Object.entries(terminalTransactionData.byLocation).map(([location, data]) => (
                          <Box key={location} sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                              {location}
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 1 }}>
                              <Grid item xs={3}>
                                <Typography variant="body2" color="textSecondary">
                                  Terminals: {data.terminals.join(', ')}
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography variant="body2" color="textSecondary">
                                  Transactions: {data.transactionCount}
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography variant="body2" color="textSecondary">
                                  Amount: ${parseFloat(data.totalAmount || 0).toFixed(2)}
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography variant="body2" color="textSecondary">
                                  Surcharge: ${parseFloat(data.totalSurcharge || 0).toFixed(2)}
                                </Typography>
                              </Grid>
                            </Grid>
                            <Divider />
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* TrxLog Details */}
                {trxLogData && trxLogData.transactions && (
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Transaction Log Details ({trxLogData.count} transactions)
                        </Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Date/Time</TableCell>
                                <TableCell>Terminal</TableCell>
                                <TableCell>Card Number</TableCell>
                                <TableCell align="right">Requested</TableCell>
                                <TableCell align="right">Dispensed</TableCell>
                                <TableCell align="right">Surcharge</TableCell>
                                <TableCell align="right">Total</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Response</TableCell>
                                <TableCell>Sequence Number</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {trxLogData.transactions.map((trx, index) => (
                                <TableRow key={index} hover>
                                  <TableCell>
                                    <Typography variant="body2">
                                      {trx.trx_date_time}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight="bold">
                                      {trx.terminal}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">
                                      ****{trx.card_last_four || trx.card_number?.slice(-4)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    ${parseFloat(trx.req_amt || 0).toFixed(2)}
                                  </TableCell>
                                  <TableCell align="right">
                                    ${parseFloat(trx.disp_amt || trx.req_amt || 0).toFixed(2)}
                                  </TableCell>
                                  <TableCell align="right">
                                    ${parseFloat(trx.surch_amt || 0).toFixed(2)}
                                  </TableCell>
                                  <TableCell align="right">
                                    ${parseFloat((trx.req_amt || 0) + (trx.surch_amt || 0)).toFixed(2)}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={trx.trx_type || 'WTH'}
                                      size="small"
                                      color={transactionTypeColor(trx.trx_type || 'WTH')}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={trx.response || 'Approved'}
                                      size="small"
                                      color={trx.response === 'Approved' ? 'success' : 'error'}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">
                                      {trx.auth_code || '-'}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Reconciliation Results */}
                {reconcileData && (
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom color="warning.main">
                          Reconciliation Report - {format(terminalReportDate, 'MM/dd/yyyy')}
                        </Typography>
                        
                        {/* Summary Statistics */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={2}>
                            <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                              <Typography variant="subtitle2" color="text.secondary">
                                ATM (Settled)
                              </Typography>
                              <Typography variant="h5">
                                {reconcileData.summary?.totalAtm || 0}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                ${reconcileData.atmTransactions?.totalAmount?.toFixed(2) || '0.00'}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={2}>
                            <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                              <Typography variant="subtitle2" color="text.secondary">
                                PAI (API)
                              </Typography>
                              <Typography variant="h5">
                                {reconcileData.summary?.totalPai || 0}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                ${reconcileData.paiTransactions?.totalAmount?.toFixed(2) || '0.00'}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={2}>
                            <Paper sx={{ p: 2, bgcolor: reconcileData.summary?.matched > 0 ? 'success.light' : 'grey.100' }}>
                              <Typography variant="subtitle2" color="text.secondary">
                                Matched
                              </Typography>
                              <Typography variant="h5" color={reconcileData.summary?.matched > 0 ? 'success.main' : 'inherit'}>
                                {reconcileData.summary?.matched || 0}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={2}>
                            <Paper sx={{ p: 2, bgcolor: reconcileData.summary?.amountMismatch > 0 ? 'warning.light' : 'grey.100' }}>
                              <Typography variant="subtitle2" color="text.secondary">
                                Mismatched
                              </Typography>
                              <Typography variant="h5" color={reconcileData.summary?.amountMismatch > 0 ? 'warning.main' : 'inherit'}>
                                {reconcileData.summary?.amountMismatch || 0}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={2}>
                            <Paper sx={{ p: 2, bgcolor: reconcileData.summary?.onlyInAtm > 0 ? 'error.light' : 'grey.100' }}>
                              <Typography variant="subtitle2" color="text.secondary">
                                Only ATM
                              </Typography>
                              <Typography variant="h5" color={reconcileData.summary?.onlyInAtm > 0 ? 'error.main' : 'inherit'}>
                                {reconcileData.summary?.onlyInAtm || 0}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={2}>
                            <Paper sx={{ p: 2, bgcolor: reconcileData.summary?.onlyInPai > 0 ? 'error.light' : 'grey.100' }}>
                              <Typography variant="subtitle2" color="text.secondary">
                                Only PAI
                              </Typography>
                              <Typography variant="h5" color={reconcileData.summary?.onlyInPai > 0 ? 'error.main' : 'inherit'}>
                                {reconcileData.summary?.onlyInPai || 0}
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>

                        {/* Transaction Details */}
                        {reconcileData.transactions && reconcileData.transactions.length > 0 && (
                          <>
                            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                              Transaction-Level Reconciliation
                            </Typography>
                            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                              <Table stickyHeader size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Terminal</TableCell>
                                    <TableCell>Sequence</TableCell>
                                    <TableCell>Date/Time</TableCell>
                                    <TableCell align="right">Requested</TableCell>
                                    <TableCell align="right">Surcharge</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                    <TableCell>Settled Date</TableCell>
                                    <TableCell align="right">PAI Amount</TableCell>
                                    <TableCell align="right">Difference</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {reconcileData.transactions.map((trx, index) => (
                                    <TableRow key={index} sx={{ 
                                      bgcolor: trx.status === 'MATCHED' ? 'success.light' : 
                                               trx.status === 'AMOUNT_MISMATCH' ? 'warning.light' : 
                                               trx.status === 'ONLY_IN_ATM' ? 'error.light' :
                                               'info.light'
                                    }}>
                                      <TableCell>
                                        <Chip 
                                          label={trx.status === 'MATCHED' ? '✓' : 
                                                 trx.status === 'AMOUNT_MISMATCH' ? '±' : 
                                                 trx.status === 'ONLY_IN_ATM' ? 'ATM' : 'PAI'}
                                          size="small"
                                          color={trx.status === 'MATCHED' ? 'success' : 
                                                 trx.status === 'AMOUNT_MISMATCH' ? 'warning' : 
                                                 trx.status === 'ONLY_IN_ATM' ? 'error' : 'info'}
                                        />
                                      </TableCell>
                                      <TableCell>{trx.terminal}</TableCell>
                                      <TableCell>{trx.sequence}</TableCell>
                                      <TableCell>{trx.datetime ? format(new Date(trx.datetime), 'MM/dd HH:mm:ss') : '-'}</TableCell>
                                      <TableCell align="right">${trx.requestedAmount?.toFixed(2) || '0.00'}</TableCell>
                                      <TableCell align="right">${trx.surchargeAmount?.toFixed(2) || '0.00'}</TableCell>
                                      <TableCell align="right">${trx.totalAmount?.toFixed(2) || '0.00'}</TableCell>
                                      <TableCell>{trx.settledDate || '-'}</TableCell>
                                      <TableCell align="right">
                                        {trx.paiAmount ? `$${trx.paiAmount.toFixed(2)}` : '-'}
                                      </TableCell>
                                      <TableCell align="right" sx={{ 
                                        fontWeight: trx.difference ? 'bold' : 'normal',
                                        color: trx.difference ? 'error.main' : 'inherit'
                                      }}>
                                        {trx.difference ? `$${Math.abs(trx.difference).toFixed(2)}` : '-'}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </>
                        )}

                        {/* Reconciliation Rate */}
                        {reconcileData.summary && (
                          <Alert 
                            severity={reconcileData.summary.reconciliationRate === '100.00%' ? 'success' : 'warning'} 
                            sx={{ mt: 2 }}
                          >
                            Reconciliation Rate: {reconcileData.summary.reconciliationRate}
                            {reconcileData.summary.reconciliationRate === '100.00%' && ' - Perfect Match!'}
                          </Alert>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Box>
          </LocalizationProvider>
        )}

        {/* Interchange Tab (tab 6) */}
        {tabValue === 'interchange' && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ mt: 3 }}>
              <InterchangeTab />
            </Box>
          </LocalizationProvider>
        )}

        {/* Settlement Batches Tab (tab 7) */}
        {tabValue === 'batches' && (
          <Box sx={{ mt: 3 }}>
            <SettlementBatches />
          </Box>
        )}

        {/* Settings Tab - Consolidated (tab 8) */}
        {tabValue === 'settings' && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ mt: 3 }}>
            <Grid container spacing={3}>
              {/* API Status Card */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      API Status
                    </Typography>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Typography variant="body1">PAI API Integration</Typography>
                      <Chip
                        icon={paiSettings.api_enabled?.value === 'true' ? <CheckCircleIcon /> : <ErrorIcon />}
                        label={paiSettings.api_enabled?.value === 'true' ? 'Enabled' : 'Disabled'}
                        color={paiSettings.api_enabled?.value === 'true' ? 'success' : 'default'}
                      />
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={paiSettings.api_enabled?.value === 'true'}
                          onChange={(e) => updatePaiSettings('api_enabled', e.target.checked)}
                          disabled={updatingSettings}
                        />
                      }
                      label="Enable PAI API"
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Schedule Card */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Daily Schedule
                    </Typography>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Typography variant="body1">Automatic Downloads</Typography>
                      <Chip
                        icon={<ScheduleIcon />}
                        label={paiSettings.schedule_time?.value || '04:00'} 
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={paiSettings.daily_schedule_enabled?.value === 'true'}
                          onChange={(e) => updatePaiSettings('daily_schedule_enabled', e.target.checked)}
                          disabled={updatingSettings || paiSettings.api_enabled?.value !== 'true'}
                        />
                      }
                      label="Enable Daily Downloads (4:00 AM EST)"
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Manual Download Card */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Manual Download Operations
                    </Typography>
                    
                    {/* Account Selection */}
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>PAI Account</InputLabel>
                        <Select
                          value={selectedAccount}
                          onChange={(e) => setSelectedAccount(e.target.value)}
                          label="PAI Account"
                        >
                          {availableAccounts.map((account) => (
                            <MenuItem key={account.id} value={account.id}>
                              {account.name}
                              {!account.hasReportGuid && (
                                <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                                  (Missing Report ID)
                                </Typography>
                              )}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {selectedAccount && !availableAccounts.find(a => a.id === selectedAccount)?.hasReportGuid && (
                        <Alert severity="warning" sx={{ flex: 1 }}>
                          This account needs a Document ID/Serial Number configured in the backend.
                        </Alert>
                      )}
                    </Box>
                    
                    {/* Download Mode Selection */}
                    <ToggleButtonGroup
                      value={downloadMode}
                      exclusive
                      onChange={(e, newMode) => newMode && setDownloadMode(newMode)}
                      sx={{ mb: 2 }}
                      size="small"
                    >
                      <ToggleButton value="yesterday">
                        Yesterday
                      </ToggleButton>
                      <ToggleButton value="specific">
                        Specific Date
                      </ToggleButton>
                      <ToggleButton value="range">
                        Date Range
                      </ToggleButton>
                    </ToggleButtonGroup>
                    
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      {downloadMode === 'specific' && (
                        <DatePicker
                          label="Download Date"
                          value={downloadDate}
                          onChange={(newDate) => setDownloadDate(newDate)}
                          renderInput={(params) => <TextField {...params} size="small" />}
                          maxDate={new Date()}
                        />
                      )}
                      
                      {downloadMode === 'range' && (
                        <>
                          <DatePicker
                            label="Start Date"
                            value={downloadStartDate}
                            onChange={(newDate) => setDownloadStartDate(newDate)}
                            renderInput={(params) => <TextField {...params} size="small" />}
                            maxDate={new Date()}
                          />
                          <DatePicker
                            label="End Date"
                            value={downloadEndDate}
                            onChange={(newDate) => setDownloadEndDate(newDate)}
                            renderInput={(params) => <TextField {...params} size="small" />}
                            maxDate={new Date()}
                            minDate={downloadStartDate}
                          />
                        </>
                      )}
                      
                      <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={() => {
                          if (downloadMode === 'yesterday') {
                            handleDownloadReport('downloadYesterday');
                          } else if (downloadMode === 'specific') {
                            handleDownloadReport('download', downloadDate);
                          } else if (downloadMode === 'range') {
                            handleDownloadReport('downloadRange');
                          }
                        }}
                        disabled={paiSettings.api_enabled?.value !== 'true' || downloadingReport}
                      >
                        {downloadingReport ? 'Downloading...' : 'Download Report'}
                      </Button>
                    </Box>

                    {/* Display last download statistics */}
                    {lastDownloadStats && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Last Download Statistics:
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Date: {lastDownloadStats.date}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Time: {lastDownloadStats.timestamp}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="success.main">
                              Records Inserted: {lastDownloadStats.recordsProcessed}
                            </Typography>
                            {lastDownloadStats.duplicates > 0 && (
                              <Typography variant="body2" color="warning.main">
                                Duplicates Skipped: {lastDownloadStats.duplicates}
                              </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary">
                              Total Records Found: {lastDownloadStats.totalRecords}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}

                    {paiSettings.last_successful_run?.value && (
                      <Typography variant="caption" color="text.secondary" display="block" mb={1} mt={2}>
                        System Last Run: {new Date(paiSettings.last_successful_run.value).toLocaleString()}
                      </Typography>
                    )}

                    {paiSettings.last_error?.value && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        Last error: {paiSettings.last_error.value}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* NACHA Settings Section */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <BankIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      NACHA Settings
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <NachaSettingsForm />
                  </CardContent>
                </Card>
              </Grid>

              {/* Frequent Accounts Section */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <StarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Frequent Accounts
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <PaiFrequentAccounts />
                  </CardContent>
                </Card>
              </Grid>

              {/* HG/GH Terminal Matcher Section */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <SyncIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      HG/GH Terminal Matcher
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <HgGhMatcher />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
          </LocalizationProvider>
        )}

      </Paper>

      {/* Mapping Dialog */}
      <Dialog open={mappingDialogOpen} onClose={handleCloseMappingDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingMapping ? 'Edit Account Mapping' : 'Add Account Mapping'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Business Code"
                value={mappingFormData.businessCode}
                onChange={(e) => setMappingFormData({ ...mappingFormData, businessCode: e.target.value })}
                disabled={!!editingMapping}
                required
                helperText="PAI business code"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Business Name"
                value={mappingFormData.businessName}
                onChange={(e) => setMappingFormData({ ...mappingFormData, businessName: e.target.value })}
                helperText="Optional"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Account Number"
                value={mappingFormData.fullAccountNumber}
                onChange={(e) => setMappingFormData({ ...mappingFormData, fullAccountNumber: e.target.value })}
                required
                type={showAccountNumbers ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      size="small"
                      onClick={() => setShowAccountNumbers(!showAccountNumbers)}
                    >
                      {showAccountNumbers ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Routing Number"
                value={mappingFormData.fullRoutingNumber}
                onChange={(e) => setMappingFormData({ ...mappingFormData, fullRoutingNumber: e.target.value })}
                inputProps={{ maxLength: 9 }}
                helperText="9 digits"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Terminal IDs"
                value={mappingFormData.terminalIds}
                onChange={(e) => setMappingFormData({ ...mappingFormData, terminalIds: e.target.value })}
                helperText="Comma-separated terminal IDs (optional)"
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={mappingFormData.notes}
                onChange={(e) => setMappingFormData({ ...mappingFormData, notes: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={mappingFormData.active}
                    onChange={(e) => setMappingFormData({ ...mappingFormData, active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMappingDialog}>Cancel</Button>
          <Button
            onClick={handleSaveMapping}
            variant="contained"
            disabled={
              !mappingFormData.businessCode ||
              !mappingFormData.fullAccountNumber ||
              !mappingFormData.fullRoutingNumber ||
              mappingFormData.fullRoutingNumber.length !== 9
            }
          >
            {editingMapping ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Run Settlement Confirmation Dialog */}
      <Dialog open={runSettlementOpen} onClose={() => setRunSettlementOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PlayArrowIcon color="success" />
            Run Settlement
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Generate a settlement report for <strong>all {paiLocations.length} locations</strong>.
          </Typography>

          {/* Date Selector */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Settlement Date:
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={runSettlementDate}
                onChange={(newValue) => setRunSettlementDate(newValue)}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </LocalizationProvider>
          </Box>

          {/* Report Type Toggles */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Report Types:
            </Typography>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={runSettlementTypes.settlement}
                    onChange={(e) => setRunSettlementTypes(prev => ({ ...prev, settlement: e.target.checked }))}
                    color="success"
                  />
                }
                label="Settlement (Withdrawals)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={runSettlementTypes.surcharge}
                    onChange={(e) => setRunSettlementTypes(prev => ({ ...prev, surcharge: e.target.checked }))}
                    color="info"
                  />
                }
                label="Surcharge"
              />
            </FormGroup>
          </Box>

          {/* Processor Cutoff Time */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Processor Cutoff Time:
            </Typography>
            <ToggleButtonGroup
              value={runSettlementCutoff}
              exclusive
              onChange={(e, newValue) => newValue && setRunSettlementCutoff(newValue)}
              size="small"
              fullWidth
            >
              <ToggleButton value="processor" sx={{ textTransform: 'none' }}>
                Processor (3pm cutoff)
              </ToggleButton>
              <ToggleButton value="calendar" sx={{ textTransform: 'none' }}>
                Calendar (12am - 12am)
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {runSettlementCutoff === 'processor'
                ? 'Transactions after 3pm EST settle on the next day'
                : 'Standard calendar day (midnight to midnight)'}
            </Typography>
          </Box>

          {/* Locations List */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Locations to be included:
            </Typography>
            <Box sx={{
              maxHeight: 150,
              overflowY: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1,
              backgroundColor: 'grey.50'
            }}>
              {paiLocations.map((location) => (
                <Chip
                  key={location}
                  label={location}
                  size="small"
                  sx={{ m: 0.5 }}
                  icon={<CheckCircleIcon />}
                  color="success"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>

          {!runSettlementTypes.settlement && !runSettlementTypes.surcharge && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Please select at least one report type.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunSettlementOpen(false)} disabled={settlementLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleRunPaiSettlement}
            variant="contained"
            color="success"
            disabled={settlementLoading || paiLocations.length === 0 || (!runSettlementTypes.settlement && !runSettlementTypes.surcharge)}
            startIcon={settlementLoading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
          >
            {settlementLoading ? 'Generating...' : `Run for ${paiLocations.length} locations`}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default PaiDashboard;
